<template>
  <div class="home accountant-page">
    <div>
            <div>
                
                <section>
                    <div class="accounting-hero py-8 px-8">
                        <div class="accounting-hero-layout">
                            <div>
                                <h4 class="accounting-title">{{ tx('accounting_workspace', 'Accounting workspace') }}</h4>
                                <p class="accounting-subtitle">{{ tx('accounting_workspace_desc', 'Review VAT, payments, invoices, and closing figures from one desk.') }}</p>
                            </div>
                            <div class="accounting-hero-actions">
                                <div v-if="activeTab === 'overview'" class="accounting-period-control">
                                    <label for="overview-period" class="accounting-control-label">{{ tx('reporting_period', 'Reporting period') }}</label>
                                    <select id="overview-period" v-model="overviewDateRangeOption" @change="handleOverviewDateRangeChange" class="form-select accounting-period-select">
                                        <option value="">{{$t('filter_by_date')}}</option>
                                        <option value="today">{{$t('today')}}</option>
                                        <option value="yesterday">{{$t('yesterday')}}</option>
                                        <option value="this_week">{{$t('this_week')}}</option>
                                        <option value="last_week">{{$t('last_week')}}</option>
                                        <option value="this_month">{{$t('this_month')}}</option>
                                        <option value="custom">{{$t('custom_date')}}</option>
                                    </select>
                                </div>
                                <button v-if="activeTab === 'overview'" class="btn btn-white border shadow-sm accounting-refresh-btn" type="button" @click="getStats" :disabled="loadingStats" :title="tx('refresh_data', 'Refresh data')">
                                    <AppIcon name="bi-arrow-clockwise" class="bi bi-arrow-clockwise" :class="{ 'accounting-spin': loadingStats }" />
                                    <span class="visually-hidden">{{ tx('refresh_data', 'Refresh data') }}</span>
                                </button>
                                <button v-if="activeTab === 'overview'" class="btn btn-primary accounting-export-btn" type="button" @click="exportVATDeclaration" :disabled="!salesStats || !purchasesStats || loadingStats">
                                    <AppIcon name="bi-download" class="bi bi-download me-1" />{{ tx('export_vat_declaration', 'Export VAT declaration') }}
                                </button>
                            </div>
                        </div>
                        <div v-if="overviewDateRangeOption === 'custom'" class="accounting-custom-period">
                            <span class="small text-muted">{{ tx('choose_period_dates', 'Choose the start and end dates for this report.') }}</span>
                            <date-picker :clearable="true" :editable="false" class="accounting-date-picker" v-model="overviewRange" type="date" range :multi-calendars="1" :placeholder="$t('select_date_range')"></date-picker>
                        </div>
                    </div>
                </section>
            </div>
      
            <!-- Content -->
            <section class="">
                <div class="px-8">

                    <!-- Tabs -->
                    <ul class="nav nav-tabs accounting-tabs mb-4" role="tablist" :aria-label="tx('accounting_sections', 'Accounting sections')">
                      <li class="nav-item" role="presentation">
                        <button id="accounting-tab-overview" type="button" class="nav-link" role="tab" :aria-selected="activeTab === 'overview' ? 'true' : 'false'" aria-controls="accounting-panel-overview" :class="{active: activeTab === 'overview'}" @click="setActiveTab('overview')">
                          <AppIcon name="bi-grid" class="bi bi-grid me-1" />{{$t('overview')}}
                        </button>
                      </li>
                      <li class="nav-item" role="presentation">
                        <button id="accounting-tab-sales" type="button" class="nav-link" role="tab" :aria-selected="activeTab === 'sales' ? 'true' : 'false'" aria-controls="accounting-panel-sales" :class="{active: activeTab === 'sales'}" @click="setActiveTab('sales')">
                          <AppIcon name="bi-receipt" class="bi bi-receipt me-1" />{{$t('sales')}}
                          <span v-if="salesInvoicesCount" class="accounting-tab-count">{{ formatCompactNumber(salesInvoicesCount) }}</span>
                        </button>
                      </li>
                      <li class="nav-item" role="presentation">
                        <button id="accounting-tab-purchases" type="button" class="nav-link" role="tab" :aria-selected="activeTab === 'purchases' ? 'true' : 'false'" aria-controls="accounting-panel-purchases" :class="{active: activeTab === 'purchases'}" @click="setActiveTab('purchases')">
                          <AppIcon name="bi-journal-check" class="bi bi-journal-check me-1" />{{$t('purchases')}}
                          <span v-if="supplierInvoicesCount" class="accounting-tab-count">{{ formatCompactNumber(supplierInvoicesCount) }}</span>
                        </button>
                      </li>
                    </ul>

                    <!-- Overview Tab -->
                    <div v-if="activeTab === 'overview'" id="accounting-panel-overview" role="tabpanel" aria-labelledby="accounting-tab-overview">
                        <div class="accounting-section-heading">
                            <div>
                                <span class="accounting-section-kicker">{{ tx('at_a_glance', 'At a glance') }}</span>
                                <h2>{{ tx('financial_position', 'Financial position') }}</h2>
                            </div>
                            <div class="text-end">
                                <span class="accounting-period-badge"><AppIcon name="bi-calendar3" class="bi bi-calendar3 me-1" />{{ periodLabel }}</span>
                                <small v-if="statsUpdatedAt" class="accounting-updated-at">{{ tx('updated', 'Updated') }} {{ formatTimestamp(statsUpdatedAt) }}</small>
                            </div>
                        </div>

                        <div v-if="statsError" class="accounting-error-state mb-4" role="alert">
                            <span class="accounting-error-icon"><AppIcon name="bi-cloud-slash" class="bi bi-cloud-slash" /></span>
                            <div class="flex-grow-1">
                                <div class="fw-semibold">{{ tx('accounting_data_unavailable', 'Accounting data is temporarily unavailable') }}</div>
                                <small>{{ tx('accounting_data_unavailable_desc', 'Check your connection and try refreshing this report.') }}</small>
                            </div>
                            <button class="btn btn-sm btn-outline-danger" type="button" @click="getStats">{{ tx('try_again', 'Try again') }}</button>
                        </div>

                        <div class="accounting-kpi-grid mb-4" :aria-busy="loadingStats ? 'true' : 'false'">
                            <div v-for="card in accountantKpis" :key="card.key" class="accounting-kpi" :class="[card.iconClass, { 'is-loading': loadingStats && !salesStats && !purchasesStats }]">
                                <div class="d-flex align-items-start justify-content-between">
                                    <div class="accounting-kpi-copy">
                                        <span class="accounting-kpi-label">{{ card.label }}</span>
                                        <span v-if="loadingStats && !salesStats && !purchasesStats" class="accounting-skeleton accounting-skeleton-value"></span>
                                        <h3 v-else class="accounting-kpi-value" :class="card.valueClass">{{ card.value }}</h3>
                                        <span v-if="loadingStats && !salesStats && !purchasesStats" class="accounting-skeleton accounting-skeleton-helper"></span>
                                        <small v-else class="text-muted">{{ card.helper }}</small>
                                    </div>
                                    <span class="accounting-kpi-icon" :class="card.iconClass">
                                        <AppIcon :name="card.icon" class="bi" :class="card.icon" />
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div class="row mb-4">
                            <div class="col-lg-8 mb-3 mb-lg-0">
                                <div class="accounting-panel h-100">
                                    <div class="d-flex align-items-center justify-content-between mb-3">
                                        <h6 class="mb-0">{{ tx('closing_review', 'Closing review') }}</h6>
                                        <span class="badge bg-light text-dark">{{ periodLabel }}</span>
                                    </div>
                                    <div class="row">
                                        <div v-for="item in reviewQueues" :key="item.key" class="col-md-4 mb-3 mb-md-0">
                                            <div class="accounting-review-item">
                                                <div class="d-flex align-items-center justify-content-between mb-2">
                                                    <span class="small text-muted">{{ item.label }}</span>
                                                    <AppIcon :name="[item.icon, item.iconClass]" class="bi" :class="[item.icon, item.iconClass]" />
                                                </div>
                                                <h5 class="mb-1">{{ item.value }}</h5>
                                                <small class="text-muted">{{ item.helper }}</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="col-lg-4">
                                <div class="accounting-panel h-100">
                                    <div class="d-flex align-items-center justify-content-between mb-3">
                                        <h6 class="mb-0">{{ tx('accounting_alerts', 'Accounting alerts') }}</h6>
                                        <span class="badge" :class="accountingAlerts.length ? 'bg-warning text-dark' : 'bg-success'">{{ accountingAlerts.length }}</span>
                                    </div>
                                    <div v-if="accountingAlerts.length">
                                        <div v-for="alert in accountingAlerts" :key="alert.key" class="accounting-alert">
                                            <AppIcon name="bi-exclamation-circle" class="bi bi-exclamation-circle text-warning me-2" />
                                            <div>
                                                <div class="fw-semibold small">{{ alert.title }}</div>
                                                <small class="text-muted">{{ alert.text }}</small>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else class="text-center text-muted py-3">
                                        <AppIcon name="bi-check2-circle" class="bi bi-check2-circle text-success" style="font-size: 28px;" />
                                        <div class="small mt-2">{{ tx('accounting_no_alerts', 'No urgent accounting alerts for this period.') }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div v-if="loadingStats && (salesStats || purchasesStats)" class="accounting-inline-loading" role="status">
                            <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                            {{ tx('refreshing_report', 'Refreshing report…') }}
                        </div>
                        
                        <div>
                            <!-- Sales Insights -->
                            <div class="card mb-4" v-if="salesStats">
                                <div class="card-header bg-white">
                                    <h6 class="mb-0">{{$t('sales')}} {{$t('insights')}}</h6>
                                </div>
                                <div class="card-body">
                                    <div class="row">
                                        <div class="col-md-8">
                                            <div class="row">
                                                <div class="col-md-3 mb-3">
                                                    <small class="text-muted d-block">{{$t('total_sales')}}</small>
                                                    <h4 class="text-primary">{{formatMoney(salesStats.summary.totalSales)}}</h4>
                                                </div>
                                                <div class="col-md-3 mb-3">
                                                    <small class="text-muted d-block">{{$t('paid')}}</small>
                                                    <h4 class="text-success">{{formatMoney(salesStats.status.paid.amount)}}</h4>
                                                </div>
                                                <div class="col-md-3 mb-3">
                                                    <small class="text-muted d-block">{{$t('not_paid')}}</small>
                                                    <h4 class="text-warning">{{formatMoney(salesStats.dueInvoices.amount)}}</h4>
                                                </div>
                                                 <div class="col-md-3 mb-3">
                                                    <small class="text-muted d-block">{{$t('canceled')}}</small>
                                                    <h4 class="text-danger">{{formatMoney(salesStats.status.canceled.amount)}}</h4>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="col-md-4 border-start">
                                            <h6 class="text-center mb-3">{{$t('payment_methods')}}</h6>
                                            <div v-if="salesStats.paymentMethods && salesStats.paymentMethods.length">
                                                <ul class="list-group list-group-flush small">
                                                    <li v-for="(pm, i) in salesStats.paymentMethods" :key="i" class="list-group-item d-flex justify-content-between align-items-center px-0 border-0">
                                                        <span>{{$t(pm.method)}}</span>
                                                        <span class="fw-bold">{{formatMoney(pm.amount)}}</span>
                                                    </li>
                                                </ul>
                                            </div>
                                            <div v-else class="text-center text-muted small mt-4">
                                                {{$t('no_data')}}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <!-- Purchases Insights -->
                            <div class="card mb-4" v-if="purchasesStats">
                                <div class="card-header bg-white">
                                    <h6 class="mb-0">{{$t('purchases')}} {{$t('insights')}}</h6>
                                </div>
                                <div class="card-body">
                                    <div class="row">
                                        <div class="col-md-8">
                                            <div class="row">
                                                <div class="col-md-3 mb-3">
                                                    <small class="text-muted d-block">{{$t('total_purchases')}}</small>
                                                    <h4 class="text-primary">{{formatMoney(purchasesStats.summary.totalPurchases)}}</h4>
                                                </div>
                                                <div class="col-md-3 mb-3">
                                                    <small class="text-muted d-block">{{$t('paid')}}</small>
                                                    <h4 class="text-success">{{formatMoney(purchasesStats.status.paid.amount)}}</h4>
                                                </div>
                                                <div class="col-md-3 mb-3">
                                                    <small class="text-muted d-block">{{$t('not_paid')}}</small>
                                                    <h4 class="text-warning">{{formatMoney(purchasesStats.dueInvoices.amount)}}</h4>
                                                </div>
                                                 <div class="col-md-3 mb-3">
                                                    <small class="text-muted d-block">{{$t('canceled')}}</small>
                                                    <h4 class="text-danger">{{formatMoney(purchasesStats.status.canceled.amount)}}</h4>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="col-md-4 border-start">
                                            <h6 class="text-center mb-3">{{$t('payment_methods')}}</h6>
                                            <div v-if="purchasesStats.paymentMethods && purchasesStats.paymentMethods.length">
                                                <ul class="list-group list-group-flush small">
                                                    <li v-for="(pm, i) in purchasesStats.paymentMethods" :key="i" class="list-group-item d-flex justify-content-between align-items-center px-0 border-0">
                                                        <span>{{$t(pm.method)}}</span>
                                                        <span class="fw-bold">{{formatMoney(pm.amount)}}</span>
                                                    </li>
                                                </ul>
                                            </div>
                                            <div v-else class="text-center text-muted small mt-4">
                                                {{$t('no_data')}}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Profit & Loss Card -->
                        <div class="card mb-4 border-0 shadow-sm" v-if="salesStats && purchasesStats">
                            <div class="card-header bg-white d-flex justify-content-between align-items-center">
                                <h6 class="mb-0"><AppIcon name="bi-bar-chart-line" class="bi bi-bar-chart-line me-2 text-primary" />{{$t('profit_loss_statement')}}</h6>
                                <button class="btn btn-sm btn-outline-primary" @click="exportVATDeclaration">
                                    <AppIcon name="bi-download" class="bi bi-download me-1" />{{$t('export_vat_declaration')}}
                                </button>
                            </div>
                            <div class="card-body">
                                <div class="row">
                                    <div class="col-md-4">
                                        <div class="accounting-pl-summary is-positive">
                                            <small class="text-muted d-block fw-semibold">{{$t('total_revenue_ht')}}</small>
                                            <h4 class="text-success mb-0 fw-bold">{{formatMoney(plSummary.revenue)}}</h4>
                                            <small class="text-muted">{{salesStats.summary?.invoiceCount || 0}} {{$t('invoices')}}</small>
                                        </div>
                                    </div>
                                    <div class="col-md-4">
                                        <div class="accounting-pl-summary is-negative">
                                            <small class="text-muted d-block fw-semibold">{{$t('total_expenses_ht')}}</small>
                                            <h4 class="text-danger mb-0 fw-bold">{{formatMoney(plSummary.expenses)}}</h4>
                                            <small class="text-muted">{{purchasesStats.summary?.invoiceCount || 0}} {{$t('invoices')}}</small>
                                        </div>
                                    </div>
                                    <div class="col-md-4">
                                        <div class="accounting-pl-summary" :class="plSummary.grossProfit >= 0 ? 'is-positive' : 'is-negative'">
                                            <small class="text-muted d-block fw-semibold">{{$t('gross_profit')}}</small>
                                            <h4 :class="plSummary.grossProfit >= 0 ? 'text-success' : 'text-danger'" class="mb-0 fw-bold">{{formatMoney(plSummary.grossProfit)}}</h4>
                                        </div>
                                    </div>
                                </div>
                                <!-- VAT Summary -->
                                <div class="row mt-3">
                                    <div class="col-md-4">
                                        <div class="accounting-vat-metric">
                                            <small class="text-muted d-block">{{$t('vat_collected')}}</small>
                                            <h5 class="text-primary mb-0">{{formatMoney(plSummary.vatCollected)}}</h5>
                                        </div>
                                    </div>
                                    <div class="col-md-4">
                                        <div class="accounting-vat-metric">
                                            <small class="text-muted d-block">{{$t('vat_deductible')}}</small>
                                            <h5 class="text-info mb-0">{{formatMoney(plSummary.vatDeductible)}}</h5>
                                        </div>
                                    </div>
                                    <div class="col-md-4">
                                        <div class="accounting-vat-metric" :class="plSummary.vatDue >= 0 ? 'is-due' : 'is-credit'">
                                            <small class="text-muted d-block fw-bold">{{$t('vat_due')}}</small>
                                            <h5 :class="plSummary.vatDue >= 0 ? 'text-danger' : 'text-success'" class="mb-0 fw-bold">{{formatMoney(plSummary.vatDue)}}</h5>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Cash Flow & Bank Accounts Summary -->
                        <!-- <div class="row mb-4" v-if="bankSummary">
                            <div class="col-md-8">
                                <div class="card border-0 shadow-sm h-100">
                                    <div class="card-header bg-white">
                                        <h6 class="mb-0"><AppIcon name="bi-arrow-left-right" class="bi bi-arrow-left-right me-2 text-info" />{{$t('cash_flow_overview')}}</h6>
                                    </div>
                                    <div class="card-body">
                                        <div class="row">
                                            <div class="col-md-4">
                                                <div class="text-center p-3">
                                                    <div class="rounded-circle mx-auto d-flex align-items-center justify-content-center mb-2" style="width:50px;height:50px;background:rgba(25,135,84,0.1)">
                                                        <AppIcon name="bi-arrow-down-circle" class="bi bi-arrow-down-circle text-success" style="font-size:24px" />
                                                    </div>
                                                    <small class="text-muted d-block">{{$t('incoming_total')}}</small>
                                                    <h4 class="text-success mb-0">{{formatMoney(bankSummary.cash_flow?.incoming || 0)}}</h4>
                                                </div>
                                            </div>
                                            <div class="col-md-4">
                                                <div class="text-center p-3">
                                                    <div class="rounded-circle mx-auto d-flex align-items-center justify-content-center mb-2" style="width:50px;height:50px;background:rgba(220,53,69,0.1)">
                                                        <AppIcon name="bi-arrow-up-circle" class="bi bi-arrow-up-circle text-danger" style="font-size:24px" />
                                                    </div>
                                                    <small class="text-muted d-block">{{$t('outgoing_total')}}</small>
                                                    <h4 class="text-danger mb-0">{{formatMoney(bankSummary.cash_flow?.outgoing || 0)}}</h4>
                                                </div>
                                            </div>
                                            <div class="col-md-4">
                                                <div class="text-center p-3">
                                                    <div class="rounded-circle mx-auto d-flex align-items-center justify-content-center mb-2" :style="'width:50px;height:50px;background:' + ((bankSummary.cash_flow?.net || 0) >= 0 ? 'rgba(25,135,84,0.1)' : 'rgba(220,53,69,0.1)')">
                                                        <AppIcon :name="(bankSummary.cash_flow?.net || 0) >= 0 ? 'bi-graph-up text-success' : 'bi-graph-down text-danger'" class="bi" :class="(bankSummary.cash_flow?.net || 0) >= 0 ? 'bi-graph-up text-success' : 'bi-graph-down text-danger'" style="font-size:24px" />
                                                    </div>
                                                    <small class="text-muted d-block">{{$t('net_flow_total')}}</small>
                                                    <h4 :class="(bankSummary.cash_flow?.net || 0) >= 0 ? 'text-success' : 'text-danger'" class="mb-0">{{formatMoney(bankSummary.cash_flow?.net || 0)}}</h4>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <div class="card border-0 shadow-sm h-100">
                                    <div class="card-header bg-white">
                                        <h6 class="mb-0"><AppIcon name="bi-bank" class="bi bi-bank me-2" />{{$t('total_balance')}}</h6>
                                    </div>
                                    <div class="card-body d-flex flex-column align-items-center justify-content-center">
                                        <h2 class="fw-bold text-primary mb-2">{{formatMoney(bankSummary.total_balance || 0)}}</h2>
                                        <span class="badge bg-secondary">{{bankSummary.accounts_count || 0}} {{$t('bank_accounts')}}</span>
                                        <div class="mt-3 w-100" v-if="bankSummary.accounts && bankSummary.accounts.length">
                                            <div v-for="acc in bankSummary.accounts.slice(0, 3)" :key="acc.id" class="d-flex justify-content-between align-items-center py-1 border-bottom small">
                                                <span class="text-muted">{{acc.account_name}}</span>
                                                <span class="fw-bold">{{formatMoney(acc.balance)}}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div> -->
                    </div>
                    <div v-show="activeTab === 'sales'" id="accounting-panel-sales" role="tabpanel" aria-labelledby="accounting-tab-sales">
                        <!-- Sales Filters -->
                        <div class="accounting-filter-bar mb-4">
                            <!-- Filter Dropdown -->
                            <div>
                                <div class="dropdown">
                                    <button class="btn btn-outline-secondary dropdown-toggle" type="button" id="salesFilterDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                                        <AppIcon name="bi-sliders" class="bi bi-sliders me-1" /> {{$t('filter')}}
                                        <span v-if="salesActiveFilters.length" class="accounting-filter-count">{{salesActiveFilters.length}}</span>
                                    </button>
                                    <div class="dropdown-menu accounting-filter-menu p-3" aria-labelledby="salesFilterDropdown" @click.stop>
                                        
                                        <!-- Active Filters -->
                                        <div v-if="salesActiveFilters.length > 0" class="mb-3">
                                            <div v-for="(filter, index) in salesActiveFilters" :key="index" class="mb-2">
                                                <label class="form-label small mb-1">{{$t(filter.key)}}</label>
                                                
                                                <!-- Amount Filter -->
                                                <div class="input-group input-group-sm" v-if="filter.key === 'total_amount'">
                                                    <select v-model="filter.operator" class="form-select accounting-operator-select">
                                                        <option value="gt">{{$t('operator_gt')}}</option>
                                                        <option value="lt">{{$t('operator_lt')}}</option>
                                                        <option value="eq">{{$t('operator_eq')}}</option>
                                                    </select>
                                                    <input type="number" v-model="filter.value" class="form-control form-control-sm" placeholder="0">
                                                    <button class="btn btn-outline-danger" type="button" @click="removeSalesFilter(index)">
                                                        <AppIcon name="bi-trash" class="bi bi-trash" />
                                                    </button>
                                                </div>

                                                <!-- Client Filter (Search) -->
                                                <div v-else-if="filter.key === 'client'">
                                                    <div class="d-flex">
                                                        <autocomplete
                                                            :search="searchClients"
                                                            :get-result-value="result => result.name"
                                                            :default-value="filter.label"
                                                            @submit="result => handleClientSelect(result, index)"
                                                            :placeholder="tx('search_client', 'Search client')"
                                                            class="flex-grow-1 accounting-autocomplete"
                                                        ></autocomplete>
                                                        <button class="btn btn-outline-danger btn-sm ms-1" type="button" @click="removeSalesFilter(index)">
                                                            <AppIcon name="bi-trash" class="bi bi-trash" />
                                                        </button>
                                                    </div>
                                                </div>

                                                <!-- Standard Select Filter -->
                                                <div class="input-group input-group-sm" v-else>
                                                    <select v-model="filter.value" class="form-select form-select-sm">
                                                        <option v-for="option in getSalesFilterOptions(filter.key)" :key="option.value" :value="option.value">
                                                            {{$t(option.label)}}
                                                        </option>
                                                    </select>
                                                    <button class="btn btn-outline-danger" type="button" @click="removeSalesFilter(index)">
                                                        <AppIcon name="bi-trash" class="bi bi-trash" />
                                                    </button>
                                                </div>
                                            </div>
                                            <hr class="dropdown-divider">
                                        </div>

                                        <!-- Add Filter Options -->
                                        <div class="d-grid gap-2">
                                            <button v-for="filter in salesSelectableFilters" :key="filter.key" 
                                                    class="btn btn-sm btn-outline-primary text-start" 
                                                    @click="addSalesFilter(filter)">
                                                <AppIcon name="bi-plus" class="bi bi-plus" /> {{$t(filter.key)}}
                                            </button>
                                        </div>

                                        <!-- Apply and Clear -->
                                        <div class="d-flex justify-content-between align-items-center mt-3">
                                            <button class="btn btn-sm btn-primary" @click="applySalesFilters">
                                                <AppIcon name="bi-check2" class="bi bi-check2 me-1" />{{$t('apply')}}
                                            </button>
                                            <button v-if="salesActiveFilters.length > 0" class="btn btn-link btn-sm text-decoration-none text-danger p-0" @click="clearSalesFilters">
                                                {{$t('clear_all')}}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-if="salesActiveFilters.length" class="accounting-filter-chips">
                                <span v-for="(filter, index) in salesActiveFilters" :key="`sales-chip-${filter.key}`" class="accounting-filter-chip">
                                    {{ filterSummary(filter) }}
                                    <button type="button" @click="removeSalesFilter(index)" :aria-label="`${tx('remove_filter', 'Remove filter')}: ${$t(filter.key)}`">
                                        <AppIcon name="bi-x" class="bi bi-x" />
                                    </button>
                                </span>
                                <button type="button" class="accounting-clear-filters" @click="clearSalesFilters">{{$t('clear_all')}}</button>
                            </div>
                            <span v-else class="small text-muted">{{ tx('no_active_filters', 'No additional filters applied') }}</span>
                        </div>

                    <!-- Sales Table -->
                      <div class="accounting-ledger-heading">
                            <div>
                                <span class="accounting-section-kicker">{{ tx('customer_invoices', 'Customer invoices') }}</span>
                                <h2>{{$t('sales')}}</h2>
                                <p>{{ invoiceCountLabel(salesInvoicesCount) }}</p>
                            </div>
                            <div class="accounting-ledger-actions">
                                <div class="accounting-period-control">
                                    <label for="sales-period" class="accounting-control-label">{{ tx('invoice_period', 'Invoice period') }}</label>
                                    <select id="sales-period" v-model="salesDateRangeOption" @change="handleSalesDateRangeChange" class="form-select accounting-period-select">
                                        <option value="">{{$t('filter_by_date')}}</option>
                                        <option value="today">{{$t('today')}}</option>
                                        <option value="yesterday">{{$t('yesterday')}}</option>
                                        <option value="this_week">{{$t('this_week')}}</option>
                                        <option value="last_week">{{$t('last_week')}}</option>
                                        <option value="this_month">{{$t('this_month')}}</option>
                                        <option value="custom">{{$t('custom_date')}}</option>
                                    </select>
                                </div>
                                <div v-if="salesDateRangeOption === 'custom'">
                                    <date-picker :clearable="true" :editable="false" class="accounting-ledger-date-picker" v-model="salesRange" type="date" range :multi-calendars="1" :placeholder="$t('select_date_range')"></date-picker>
                                </div>
                                <div class="dropdown">
                                  <button class="btn btn-outline-secondary accounting-export-menu" type="button" id="salesExportDropdown" data-bs-toggle="dropdown" aria-expanded="false" :disabled="!salesInvoices.length">
                                      <AppIcon name="bi-download" class="bi bi-download me-1" />{{ tx('export', 'Export') }} <AppIcon name="bi-chevron-down" class="bi bi-chevron-down ms-1" />
                                  </button>
                                  <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="salesExportDropdown">
                                      <li>
                                          <button class="dropdown-item" type="button" @click="exportSalesExcel"><AppIcon name="bi-file-earmark-spreadsheet" class="bi bi-file-earmark-spreadsheet me-2" />{{$t('excel')}}</button>
                                      </li>
                                      <li>
                                          <button class="dropdown-item" type="button" @click="exportSalesPDF"><AppIcon name="bi-file-earmark-pdf" class="bi bi-file-earmark-pdf me-2" />{{$t('pdf')}}</button>
                                      </li>
                                  </ul>
                              </div>
                            </div>
                        </div>
                    <div class="page-table-container main-table-container accounting-table-card mb-6 bg-white rounded shadow">
                        <div v-if="salesError" class="accounting-error-state m-3" role="alert">
                            <span class="accounting-error-icon"><AppIcon name="bi-cloud-slash" class="bi bi-cloud-slash" /></span>
                            <div class="flex-grow-1">
                                <div class="fw-semibold">{{ tx('invoices_unavailable', 'Invoices could not be loaded') }}</div>
                                <small>{{ tx('try_again_desc', 'Please try again in a moment.') }}</small>
                            </div>
                            <button class="btn btn-sm btn-outline-danger" type="button" @click="getSalesInvoices(salesInvoicesPage)">{{ tx('try_again', 'Try again') }}</button>
                        </div>
                       

                        <div class="table-responsive accounting-table-wrap">
                            <table class="table accounting-table mb-0 table-borderless table-striped small">
                                <thead>
                                    <tr>
                                        <!-- <th class="pt-4 pb-3 px-6">
                                            <input type="checkbox" @change="toggleSelectAll('sales', $event)" :checked="allSalesSelected">
                                        </th> -->
                                        <th class="pt-4 pb-3 px-6">ICE</th>
                                        <sortable-table-header field="client" label="client" :sort-field="salesSortField" :sort-direction="salesSortDirection" @sort="sortSales" />
                                        <sortable-table-header field="reference" label="invoice" :sort-field="salesSortField" :sort-direction="salesSortDirection" @sort="sortSales" />
                                        <sortable-table-header field="date" label="date" :sort-field="salesSortField" :sort-direction="salesSortDirection" @sort="sortSales" />
                                        <th class="pt-4 pb-3 px-6 text-end">{{$t('sub_total')}}</th>
                                        <th class="pt-4 pb-3 px-6 text-end">{{$t('vat')}}</th>
                                        <sortable-table-header field="total" label="total" :sort-field="salesSortField" :sort-direction="salesSortDirection" header-class="pt-4 pb-3 px-6 text-end" @sort="sortSales" />
                                        <th class="pt-4 pb-3 px-6">{{$t('payment_date')}}</th>
                                        <th class="pt-4 pb-3 px-6">{{$t('payment_method')}}</th>
                                        <th class="pt-4 pb-3 px-6">{{$t('reference')}}</th>
                                        <th class="pt-4 pb-3 px-6 text-end">{{$t('action')}}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="row in salesInvoices" :key="row.id">
                                        <!-- <td class="py-5 px-6">
                                            <input type="checkbox" :value="row.id" v-model="selectedSales">
                                        </td> -->
                                        <td class="py-5 px-6"><span class="accounting-identifier">{{ row?.client?.vat_number || row?.client?.tax_id || '-' }}</span></td>
                                        <td class="py-5 px-6">
                                            <router-link v-if="row?.client?.id" class="accounting-party-link" :to="`/clients/${row.client.id}`">{{ row?.client?.name || '-' }}</router-link>
                                            <span v-else>-</span>
                                        </td>
                                        <td class="py-5 px-6">
                                            <router-link class="accounting-invoice-link" :to="`/invoices/${row.id}`">{{ row.reference }}</router-link>
                                        </td>
                                        <td class="py-5 px-6">{{ formatDate(row.date) }}</td>
                                        <td class="py-5 px-6 text-end text-nowrap">{{ formatMoney(calcSubTotal(row)) }}</td>
                                        <td class="py-5 px-6 text-end text-nowrap">{{ formatMoney(calcVAT(row)) }}</td>
                                        <td class="py-5 px-6 text-end text-nowrap fw-semibold">{{ formatMoney(calcTotal(row)) }}</td>
                                        <td class="py-5 px-6">{{ paymentDate(row) }}</td>
                                        <td class="py-5 px-6"><span class="accounting-payment-method">{{ $t(paymentMethod(row)) }}</span></td>
                                        <td class="py-5 px-6"><span class="accounting-identifier">{{ paymentReference(row) }}</span></td>
                                        <td class="py-5 px-6 text-end">
                                            <button @click="openInvoiceTemplate(row, 'invoice')" type="button" class="btn btn-sm btn-outline-primary text-nowrap" :title="tx('view_invoice', 'View invoice')">
                                                <AppIcon name="bi-eye" class="bi bi-eye me-1" />{{$t('invoice')}}
                                            </button>
                                        </td> 
                                    </tr>
                                    <tr v-if="salesInvoices.length" class="accounting-total-row">
                                        <td colspan="3">{{ tx('page_total', 'Page total') }}</td>
                                        <td></td>
                                        <td class="py-5 px-6 text-end text-nowrap">{{ formatMoney(totalSalesSubTotal) }}</td>
                                        <td class="py-5 px-6 text-end text-nowrap">{{ formatMoney(totalSalesVAT) }}</td>
                                        <td class="py-5 px-6 text-end text-nowrap">{{ formatMoney(totalSalesTotal) }}</td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <table-loading-bar v-if="loadingSalesInvoices" class="page-table-state accounting-table-loading" />
                        <div v-else-if="!salesInvoices.length && !salesError" class="page-table-state accounting-empty-state">
                            <span class="accounting-empty-icon"><AppIcon name="bi-receipt" class="bi bi-receipt" /></span>
                            <h3>{{ tx('no_sales_invoices', 'No sales invoices found') }}</h3>
                            <p>{{ tx('adjust_filters_period', 'Try adjusting the period or clearing the active filters.') }}</p>
                            <button v-if="salesActiveFilters.length" type="button" class="btn btn-sm btn-outline-primary" @click="clearSalesFilters">{{$t('clear_all')}}</button>
                        </div>
                        <div v-if="salesTotalPages > 1" class="accounting-pagination">
                            <span>{{ paginationLabel(salesInvoicesPage, salesTotalPages) }}</span>
                            <div class="btn-group" role="group" :aria-label="tx('sales_pagination', 'Sales pagination')">
                                <button type="button" class="btn btn-sm btn-outline-secondary" @click="getSalesInvoices(salesInvoicesPage - 1)" :disabled="salesInvoicesPage <= 1 || loadingSalesInvoices" :title="tx('previous_page', 'Previous page')"><AppIcon name="bi-chevron-left" class="bi bi-chevron-left" /></button>
                                <button type="button" class="btn btn-sm btn-outline-secondary" @click="getSalesInvoices(salesInvoicesPage + 1)" :disabled="salesInvoicesPage >= salesTotalPages || loadingSalesInvoices" :title="tx('next_page', 'Next page')"><AppIcon name="bi-chevron-right" class="bi bi-chevron-right" /></button>
                            </div>
                        </div>
                    </div>
                    </div>
                    <div v-show="activeTab === 'purchases'" id="accounting-panel-purchases" role="tabpanel" aria-labelledby="accounting-tab-purchases">
                      <!-- Purchases Filters -->
                        <div class="accounting-filter-bar mb-4">
                            <!-- Filter Dropdown -->
                            <div>
                                <div class="dropdown">
                                    <button class="btn btn-outline-secondary dropdown-toggle" type="button" id="purchasesFilterDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                                        <AppIcon name="bi-sliders" class="bi bi-sliders me-1" /> {{$t('filter')}}
                                        <span v-if="purchasesActiveFilters.length" class="accounting-filter-count">{{purchasesActiveFilters.length}}</span>
                                    </button>
                                    <div class="dropdown-menu accounting-filter-menu p-3" aria-labelledby="purchasesFilterDropdown" @click.stop>
                                        
                                        <!-- Active Filters -->
                                        <div v-if="purchasesActiveFilters.length > 0" class="mb-3">
                                            <div v-for="(filter, index) in purchasesActiveFilters" :key="index" class="mb-2">
                                                <label class="form-label small mb-1">{{$t(filter.key)}}</label>
                                                
                                                <!-- Amount Filter -->
                                                <div class="input-group input-group-sm" v-if="filter.key === 'total_amount'">
                                                    <select v-model="filter.operator" class="form-select accounting-operator-select">
                                                        <option value="gt">{{$t('operator_gt')}}</option>
                                                        <option value="lt">{{$t('operator_lt')}}</option>
                                                        <option value="eq">{{$t('operator_eq')}}</option>
                                                    </select>
                                                    <input type="number" v-model="filter.value" class="form-control form-control-sm" placeholder="0">
                                                    <button class="btn btn-outline-danger" type="button" @click="removePurchasesFilter(index)">
                                                        <AppIcon name="bi-trash" class="bi bi-trash" />
                                                    </button>
                                                </div>

                                                <!-- Supplier Filter (Search) -->
                                                <div v-else-if="filter.key === 'supplier'">
                                                    <div class="d-flex">
                                                        <autocomplete
                                                            :search="searchSuppliers"
                                                            :get-result-value="result => result.name"
                                                            :default-value="filter.label"
                                                            @submit="result => handleSupplierSelect(result, index)"
                                                            :placeholder="tx('search_supplier', 'Search supplier')"
                                                            class="flex-grow-1 accounting-autocomplete"
                                                        ></autocomplete>
                                                        <button class="btn btn-outline-danger btn-sm ms-1" type="button" @click="removePurchasesFilter(index)">
                                                            <AppIcon name="bi-trash" class="bi bi-trash" />
                                                        </button>
                                                    </div>
                                                </div>

                                                <!-- Standard Select Filter -->
                                                <div class="input-group input-group-sm" v-else>
                                                    <select v-model="filter.value" class="form-select form-select-sm">
                                                        <option v-for="option in getPurchasesFilterOptions(filter.key)" :key="option.value" :value="option.value">
                                                            {{$t(option.label)}}
                                                        </option>
                                                    </select>
                                                    <button class="btn btn-outline-danger" type="button" @click="removePurchasesFilter(index)">
                                                        <AppIcon name="bi-trash" class="bi bi-trash" />
                                                    </button>
                                                </div>
                                            </div>
                                            <hr class="dropdown-divider">
                                        </div>

                                        <!-- Add Filter Options -->
                                        <div class="d-grid gap-2">
                                            <button v-for="filter in purchasesSelectableFilters" :key="filter.key" 
                                                    class="btn btn-sm btn-outline-primary text-start" 
                                                    @click="addPurchasesFilter(filter)">
                                                <AppIcon name="bi-plus" class="bi bi-plus" /> {{$t(filter.key)}}
                                            </button>
                                        </div>

                                        <!-- Apply and Clear -->
                                        <div class="d-flex justify-content-between align-items-center mt-3">
                                            <button class="btn btn-sm btn-primary" @click="applyPurchasesFilters">
                                                <AppIcon name="bi-check2" class="bi bi-check2 me-1" />{{$t('apply')}}
                                            </button>
                                            <button v-if="purchasesActiveFilters.length > 0" class="btn btn-link btn-sm text-decoration-none text-danger p-0" @click="clearPurchasesFilters">
                                                {{$t('clear_all')}}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-if="purchasesActiveFilters.length" class="accounting-filter-chips">
                                <span v-for="(filter, index) in purchasesActiveFilters" :key="`purchases-chip-${filter.key}`" class="accounting-filter-chip">
                                    {{ filterSummary(filter) }}
                                    <button type="button" @click="removePurchasesFilter(index)" :aria-label="`${tx('remove_filter', 'Remove filter')}: ${$t(filter.key)}`">
                                        <AppIcon name="bi-x" class="bi bi-x" />
                                    </button>
                                </span>
                                <button type="button" class="accounting-clear-filters" @click="clearPurchasesFilters">{{$t('clear_all')}}</button>
                            </div>
                            <span v-else class="small text-muted">{{ tx('no_active_filters', 'No additional filters applied') }}</span>
                        </div>

                    <!-- Purchases Table -->
                      <div class="accounting-ledger-heading">
                            <div>
                                <span class="accounting-section-kicker">{{ tx('supplier_invoices', 'Supplier invoices') }}</span>
                                <h2>{{$t('purchases')}}</h2>
                                <p>{{ invoiceCountLabel(supplierInvoicesCount) }}</p>
                            </div>
                            <div class="accounting-ledger-actions">
                                <div class="accounting-period-control">
                                    <label for="purchases-period" class="accounting-control-label">{{ tx('invoice_period', 'Invoice period') }}</label>
                                    <select id="purchases-period" v-model="purchasesDateRangeOption" @change="handlePurchasesDateRangeChange" class="form-select accounting-period-select">
                                        <option value="">{{$t('filter_by_date')}}</option>
                                        <option value="today">{{$t('today')}}</option>
                                        <option value="yesterday">{{$t('yesterday')}}</option>
                                        <option value="this_week">{{$t('this_week')}}</option>
                                        <option value="last_week">{{$t('last_week')}}</option>
                                        <option value="this_month">{{$t('this_month')}}</option>
                                        <option value="custom">{{$t('custom_date')}}</option>
                                    </select>
                                </div>
                                <div v-if="purchasesDateRangeOption === 'custom'">
                                    <date-picker :clearable="true" :editable="false" class="accounting-ledger-date-picker" v-model="purchasesRange" type="date" range :multi-calendars="1" :placeholder="$t('select_date_range')"></date-picker>
                                </div>
                                <div class="dropdown">
                                  <button class="btn btn-outline-secondary accounting-export-menu" type="button" id="purchasesExportDropdown" data-bs-toggle="dropdown" aria-expanded="false" :disabled="!supplierInvoices.length">
                                      <AppIcon name="bi-download" class="bi bi-download me-1" />{{ tx('export', 'Export') }} <AppIcon name="bi-chevron-down" class="bi bi-chevron-down ms-1" />
                                  </button>
                                  <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="purchasesExportDropdown">
                                      <li>
                                          <button class="dropdown-item" type="button" @click="exportPurchasesExcel"><AppIcon name="bi-file-earmark-spreadsheet" class="bi bi-file-earmark-spreadsheet me-2" />{{$t('excel')}}</button>
                                      </li>
                                      <li>
                                          <button class="dropdown-item" type="button" @click="exportPurchasesPDF"><AppIcon name="bi-file-earmark-pdf" class="bi bi-file-earmark-pdf me-2" />{{$t('pdf')}}</button>
                                      </li>
                                  </ul>
                              </div>
                            </div>
                        </div>
                    <div class="page-table-container main-table-container accounting-table-card mb-6 bg-white rounded shadow">
                        <div v-if="purchasesError" class="accounting-error-state m-3" role="alert">
                            <span class="accounting-error-icon"><AppIcon name="bi-cloud-slash" class="bi bi-cloud-slash" /></span>
                            <div class="flex-grow-1">
                                <div class="fw-semibold">{{ tx('invoices_unavailable', 'Invoices could not be loaded') }}</div>
                                <small>{{ tx('try_again_desc', 'Please try again in a moment.') }}</small>
                            </div>
                            <button class="btn btn-sm btn-outline-danger" type="button" @click="getSupplierInvoices(supplierInvoicesPage)">{{ tx('try_again', 'Try again') }}</button>
                        </div>
                       

                        <div class="table-responsive accounting-table-wrap">
                            <table class="table accounting-table mb-0 table-borderless table-striped small">
                                <thead>
                                    <tr>
                                        <!-- <th class="pt-4 pb-3 px-6">
                                            <input type="checkbox" @change="toggleSelectAll('purchases', $event)" :checked="allPurchasesSelected">
                                        </th> -->
                                        <th class="pt-4 pb-3 px-6">ICE</th>
                                        <sortable-table-header field="supplier" label="supplier" :sort-field="purchasesSortField" :sort-direction="purchasesSortDirection" @sort="sortPurchases" />
                                        <sortable-table-header field="reference" label="invoice" :sort-field="purchasesSortField" :sort-direction="purchasesSortDirection" @sort="sortPurchases" />
                                        <sortable-table-header field="date" label="date" :sort-field="purchasesSortField" :sort-direction="purchasesSortDirection" @sort="sortPurchases" />
                                        <th class="pt-4 pb-3 px-6 text-end">{{$t('sub_total')}}</th>
                                        <th class="pt-4 pb-3 px-6 text-end">{{$t('vat')}}</th>
                                        <sortable-table-header field="total" label="total" :sort-field="purchasesSortField" :sort-direction="purchasesSortDirection" header-class="pt-4 pb-3 px-6 text-end" @sort="sortPurchases" />
                                        <th class="pt-4 pb-3 px-6">{{$t('payment_date')}}</th>
                                        <th class="pt-4 pb-3 px-6">{{$t('payment_method')}}</th>
                                        <th class="pt-4 pb-3 px-6">{{$t('reference')}}</th>
                                        <th class="pt-4 pb-3 px-6 text-end">{{$t('action')}}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="row in supplierInvoices" :key="row.id">
                                        <!-- <td class="py-5 px-6">
                                            <input type="checkbox" :value="row.id" v-model="selectedPurchases">
                                        </td> -->
                                        <td class="py-5 px-6"><span class="accounting-identifier">{{ row?.supplier?.vat_number || row?.supplier?.tax_id || '-' }}</span></td>
                                        <td class="py-5 px-6">
                                            <router-link v-if="row?.supplier?.id" class="accounting-party-link" :to="`/suppliers/${row.supplier.id}`">{{ row?.supplier?.name || '-' }}</router-link>
                                            <span v-else>-</span>
                                        </td>
                                        <td class="py-5 px-6">
                                            <router-link class="accounting-invoice-link" :to="`/supplier-invoices/${row.id}`">{{ row.reference }}</router-link>
                                        </td>
                                        <td class="py-5 px-6">{{ formatDate(row.date) }}</td>
                                        <td class="py-5 px-6 text-end text-nowrap">{{ formatMoney(calcSubTotal(row)) }}</td>
                                        <td class="py-5 px-6 text-end text-nowrap">{{ formatMoney(calcVAT(row)) }}</td>
                                        <td class="py-5 px-6 text-end text-nowrap fw-semibold">{{ formatMoney(calcTotal(row)) }}</td>
                                        <td class="py-5 px-6">{{ paymentDate(row) }}</td>
                                        <td class="py-5 px-6"><span class="accounting-payment-method">{{ $t(paymentMethod(row)) }}</span></td>
                                        <td class="py-5 px-6"><span class="accounting-identifier">{{ paymentReference(row) }}</span></td>
                                        <td class="py-5 px-6 text-end">
                                            <button 
                                              class="btn btn-sm btn-outline-primary text-nowrap"
                                              type="button"
                                              @click="openAttachmentModal(row)"
                                            >
                                              <AppIcon name="bi-paperclip" class="bi bi-paperclip me-1" />
                                              {{$t('attachments')}}
                                            </button>
                                        </td>
                                    </tr>
                                    <tr v-if="supplierInvoices.length" class="accounting-total-row">
                                        <td colspan="3">{{ tx('page_total', 'Page total') }}</td>
                                        <td></td>
                                        <td class="py-5 px-6 text-end text-nowrap">{{ formatMoney(totalPurchasesSubTotal) }}</td>
                                        <td class="py-5 px-6 text-end text-nowrap">{{ formatMoney(totalPurchasesVAT) }}</td>
                                        <td class="py-5 px-6 text-end text-nowrap">{{ formatMoney(totalPurchasesTotal) }}</td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <table-loading-bar v-if="loadingSupplierInvoices" class="page-table-state accounting-table-loading" />
                        <div v-else-if="!supplierInvoices.length && !purchasesError" class="page-table-state accounting-empty-state">
                            <span class="accounting-empty-icon"><AppIcon name="bi-journal-text" class="bi bi-journal-text" /></span>
                            <h3>{{ tx('no_purchase_invoices', 'No purchase invoices found') }}</h3>
                            <p>{{ tx('adjust_filters_period', 'Try adjusting the period or clearing the active filters.') }}</p>
                            <button v-if="purchasesActiveFilters.length" type="button" class="btn btn-sm btn-outline-primary" @click="clearPurchasesFilters">{{$t('clear_all')}}</button>
                        </div>
                        <div v-if="purchasesTotalPages > 1" class="accounting-pagination">
                            <span>{{ paginationLabel(supplierInvoicesPage, purchasesTotalPages) }}</span>
                            <div class="btn-group" role="group" :aria-label="tx('purchases_pagination', 'Purchases pagination')">
                                <button type="button" class="btn btn-sm btn-outline-secondary" @click="getSupplierInvoices(supplierInvoicesPage - 1)" :disabled="supplierInvoicesPage <= 1 || loadingSupplierInvoices" :title="tx('previous_page', 'Previous page')"><AppIcon name="bi-chevron-left" class="bi bi-chevron-left" /></button>
                                <button type="button" class="btn btn-sm btn-outline-secondary" @click="getSupplierInvoices(supplierInvoicesPage + 1)" :disabled="supplierInvoicesPage >= purchasesTotalPages || loadingSupplierInvoices" :title="tx('next_page', 'Next page')"><AppIcon name="bi-chevron-right" class="bi bi-chevron-right" /></button>
                            </div>
                        </div>
                    </div>

                    <!-- Bank Accounts Tab -->
                    <div v-show="activeTab === 'bank_accounts'">
                        <div class="d-flex align-items-center justify-content-between mb-3">
                            <h6 class="mb-0"><AppIcon name="bi-bank" class="bi bi-bank me-2" />{{$t('all_bank_accounts')}}</h6>
                            <router-link to="/bank-accounts" class="btn btn-sm btn-outline-primary">
                                <AppIcon name="bi-box-arrow-up-right" class="bi bi-box-arrow-up-right me-1" />{{$t('view_details')}}
                            </router-link>
                        </div>

                        <!-- Bank Summary Cards -->
                        <div class="row mb-4" v-if="bankSummary">
                            <div class="col-md-3 col-sm-6 mb-2">
                                <div class="card border-0 shadow-sm h-100" style="border-left: 4px solid #0d6efd !important;">
                                    <div class="card-body py-3 text-center">
                                        <small class="text-muted d-block">{{$t('total_balance')}}</small>
                                        <h4 class="text-primary fw-bold mb-0">{{formatMoney(bankSummary.total_balance || 0)}}</h4>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-3 col-sm-6 mb-2">
                                <div class="card border-0 shadow-sm h-100" style="border-left: 4px solid #198754 !important;">
                                    <div class="card-body py-3 text-center">
                                        <small class="text-muted d-block">{{$t('incoming_total')}}</small>
                                        <h4 class="text-success fw-bold mb-0">{{formatMoney(bankSummary.cash_flow?.incoming || 0)}}</h4>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-3 col-sm-6 mb-2">
                                <div class="card border-0 shadow-sm h-100" style="border-left: 4px solid #dc3545 !important;">
                                    <div class="card-body py-3 text-center">
                                        <small class="text-muted d-block">{{$t('outgoing_total')}}</small>
                                        <h4 class="text-danger fw-bold mb-0">{{formatMoney(bankSummary.cash_flow?.outgoing || 0)}}</h4>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-3 col-sm-6 mb-2">
                                <div class="card border-0 shadow-sm h-100" :style="'border-left: 4px solid ' + ((bankSummary.cash_flow?.net || 0) >= 0 ? '#198754' : '#dc3545') + ' !important;'">
                                    <div class="card-body py-3 text-center">
                                        <small class="text-muted d-block">{{$t('net_flow_total')}}</small>
                                        <h4 :class="(bankSummary.cash_flow?.net || 0) >= 0 ? 'text-success' : 'text-danger'" class="fw-bold mb-0">{{formatMoney(bankSummary.cash_flow?.net || 0)}}</h4>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Accounts Table -->
                        <div class="card border-0 shadow-sm mb-4" v-if="bankSummary && bankSummary.accounts">
                            <div class="card-body p-0">
                                <div class="table-responsive">
                                    <table class="table table-hover mb-0">
                                        <thead class="table-light">
                                            <tr>
                                                <th>{{$t('account_name')}}</th>
                                                <th>{{$t('bank_name')}}</th>
                                                <th class="text-end">{{$t('balance')}}</th>
                                                <th class="text-end">{{$t('incoming_total')}}</th>
                                                <th class="text-end">{{$t('outgoing_total')}}</th>
                                                <th class="text-end">{{$t('net_flow_total')}}</th>
                                                <th class="text-center">{{$t('transactions_count')}}</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="acc in bankSummary.accounts" :key="'ba-'+acc.id">
                                                <td>
                                                    <div class="fw-semibold">{{acc.account_name}}</div>
                                                    <small class="text-muted">{{acc.account_number}}</small>
                                                </td>
                                                <td>{{acc.bank_name}}</td>
                                                <td class="text-end fw-bold">{{formatMoney(acc.balance)}}</td>
                                                <td class="text-end text-success">{{formatMoney(acc.incoming_sum)}}</td>
                                                <td class="text-end text-danger">{{formatMoney(acc.outgoing_sum)}}</td>
                                                <td class="text-end" :class="acc.net_flow >= 0 ? 'text-success' : 'text-danger'">
                                                    {{formatMoney(acc.net_flow)}}
                                                </td>
                                                <td class="text-center">
                                                    <span class="badge bg-light text-dark">{{acc.incoming_count + acc.outgoing_count}}</span>
                                                </td>
                                                <td>
                                                    <router-link :to="'/bank-accounts/' + acc.id" class="btn btn-sm btn-outline-primary">
                                                        <AppIcon name="bi-eye" class="bi bi-eye" />
                                                    </router-link>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <!-- No accounts -->
                        <div v-if="!bankSummary || !bankSummary.accounts || !bankSummary.accounts.length" class="text-center py-5 text-muted">
                            <AppIcon name="bi-bank" class="bi bi-bank" style="font-size: 48px;" />
                            <p class="mt-2">{{$t('no_bank_accounts')}}</p>
                            <router-link to="/bank-accounts" class="btn btn-primary btn-sm">
                                <AppIcon name="bi-plus-circle" class="bi bi-plus-circle me-1" />{{$t('add_bank_account')}}
                            </router-link>
                        </div>
                    </div>

                    </div>
                    
                </div>
            </section>
      </div>

      <!-- Invoice Template Modal (PDF renderer) -->
      <invoice-template-modal 
        v-if="invoiceModalOpen" 
        ref="invoiceTpl"
        :modalId="invoiceModalId"
        :isModalOpen="invoiceModalOpen"
        :data="invoiceModalData"
        :type="invoiceModalType"
        @modalClosed="invoiceModalOpen = false"
      />

      <!-- Attachments Modal -->
      <div v-if="attachmentsModalOpen" class="modal fade show accounting-modal-backdrop" tabindex="-1" role="dialog" aria-modal="true" aria-labelledby="accounting-attachments-title" @click.self="closeAttachmentModal">
        <div class="modal-dialog modal-xl modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 id="accounting-attachments-title" class="modal-title">{{$t('attachments')}}</h5>
              <button type="button" class="btn-close" @click="closeAttachmentModal" :aria-label="tx('close', 'Close')"></button>
            </div>
            <div class="modal-body">
              <attachments v-if="currentAttachmentEntityId" :entity_id="currentAttachmentEntityId" :hide_create="true" entity_type="invoice" />
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeAttachmentModal"><AppIcon name="bi-x-lg" class="bi bi-x-lg me-1" />{{$t('close')}}</button>
            </div>
          </div>
        </div>
      </div>
      <!-- End Attachments Modal -->

  </div>
</template>

<script>
import Autocomplete from '@trevoreyre/autocomplete-vue'
import '@trevoreyre/autocomplete-vue/dist/style.css'
import helper from '../helper'
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable'
import moment from 'moment'
import * as XLSX from 'xlsx'
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import InvoiceTemplateModal from '../components/invoiceLayout/InvoiceTemplateModal.vue'
import attachments from '../components/Attachment.vue'
import sortableList from '../mixins/sortableList'

export default {
  name: 'Accountant',
  persistedFilterState: {
    key: 'accountant',
    fields: [
      'overviewDateRangeOption', 'overviewRange', 'overviewStart', 'overviewEnd',
      'purchasesDateRangeOption', 'purchasesRange', 'purchasesStart', 'purchasesEnd', 'purchasesActiveFilters', 'purchasesSortField', 'purchasesSortDirection',
      'salesDateRangeOption', 'salesRange', 'salesStart', 'salesEnd', 'salesActiveFilters', 'salesSortField', 'salesSortDirection',
    ],
    dateFields: ['overviewRange', 'purchasesRange', 'salesRange'],
  },
  mixins: [sortableList],
  components : {
    DatePicker,
    InvoiceTemplateModal,
    attachments,
    Autocomplete
  },
  data(){
    return {
      activeTab: 'overview',
      
      // Overview filters
      overviewRange: [],
      overviewStart: null,
      overviewEnd: null,
      overviewDateRangeOption: 'this_month',
      loadingStats: false,
      statsError: null,
      statsUpdatedAt: null,
      statsRequestId: 0,
      salesStats: null,
      purchasesStats: null,
      bankSummary: null,

      // purchases filters
      purchasesRange: [],
      purchasesStart: null,
      purchasesEnd: null,
      purchasesDateRangeOption: 'this_month',
      purchasesActiveFilters: [],
      purchasesAvailableFilters: [
         { 
            key: 'payment_method', 
            options: [] 
         },
         {
            key: 'total_amount',
            options: [] 
         },
         {
             key: 'supplier',
             options: []
         }
      ],

      // sales filters
      salesRange: [],
      salesStart: null,
      salesEnd: null,
      salesDateRangeOption: 'this_month',
      salesActiveFilters: [],
      salesAvailableFilters: [
         { 
            key: 'payment_method', 
            options: [] 
         },
         {
            key: 'total_amount',
            options: [] 
         },
         {
             key: 'client',
             options: []
         }
      ],

      // Purchases (Supplier Invoices)
      supplierInvoices: [],
      supplierInvoicesCount: 0,
      supplierInvoicesPage: 1,
      supplierInvoicesPerPage: 25,
      purchasesSortField: 'created_at',
      purchasesSortDirection: 'desc',
      loadingSupplierInvoices: false,
      purchasesError: null,
      purchasesRequestId: 0,
      selectedPurchases: [],

      // Sales (Invoices)
      salesInvoices: [],
      salesInvoicesCount: 0,
      salesInvoicesPage: 1,
      salesInvoicesPerPage: 25,
      salesSortField: 'created_at',
      salesSortDirection: 'desc',
      loadingSalesInvoices: false,
      salesError: null,
      salesRequestId: 0,
      selectedSales: [],
      // invoice modal state
      invoiceModalOpen: false,
      invoiceModalData: null,
      invoiceModalType: 'invoice',
      invoiceModalId: 'accountant-invoice-modal-01',
      processingBulk: false,
      // attachments modal state
      attachmentsModalOpen: false,
      currentAttachmentEntityId: null,
    }
  },
  mounted(){
    if (window.mixpanel && typeof window.mixpanel.track === 'function') {
      window.mixpanel.track("accountant")
    }

    //check permissions
    if(helper.checkAccess('view_accountants') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_accountants') == -2) return this.$router.push({path : '/subscribe'})

    window.addEventListener('keydown', this.handleKeydown)
    this.fetchPaymentMethods()
    this.handleOverviewDateRangeChange()
    this.handlePurchasesDateRangeChange()
    this.handleSalesDateRangeChange()
  },
  beforeDestroy(){
    window.removeEventListener('keydown', this.handleKeydown)
    document.body.classList.remove('modal-open')
  },
  computed: {
    // Purchases totals
    totalPurchasesSubTotal(){
      return this.supplierInvoices.reduce((sum, inv) => sum + this.calcSubTotal(inv), 0)
    },
    totalPurchasesTotal(){
      return this.supplierInvoices.reduce((sum, inv) => sum + this.calcTotal(inv), 0)
    },
    totalPurchasesVAT(){
      return Math.max(this.totalPurchasesTotal - this.totalPurchasesSubTotal, 0)
    },
    purchasesTotalPages(){
      return Math.max(Math.ceil(this.supplierInvoicesCount / this.supplierInvoicesPerPage), 1)
    },
    // Sales totals
    totalSalesSubTotal(){
      return this.salesInvoices.reduce((sum, inv) => sum + this.calcSubTotal(inv), 0)
    },
    totalSalesTotal(){
      return this.salesInvoices.reduce((sum, inv) => sum + this.calcTotal(inv), 0)
    },
    totalSalesVAT(){
      return Math.max(this.totalSalesTotal - this.totalSalesSubTotal, 0)
    },
    salesTotalPages(){
      return Math.max(Math.ceil(this.salesInvoicesCount / this.salesInvoicesPerPage), 1)
    },
    // Select all flags
    allPurchasesSelected(){
      const ids = this.supplierInvoices.map(i => i.id)
      return ids.length > 0 && ids.every(id => this.selectedPurchases.includes(id))
    },
    allSalesSelected(){
      const ids = this.salesInvoices.map(i => i.id)
      return ids.length > 0 && ids.every(id => this.selectedSales.includes(id))
    },
    // Filters Helpers
    salesSelectableFilters() {
        return this.salesAvailableFilters.filter(f => !this.salesActiveFilters.some(active => active.key === f.key));
    },
    purchasesSelectableFilters() {
        return this.purchasesAvailableFilters.filter(f => !this.purchasesActiveFilters.some(active => active.key === f.key));
    },
    periodLabel() {
        if (this.overviewStart && this.overviewEnd) {
            return `${this.formatDate(this.overviewStart)} - ${this.formatDate(this.overviewEnd)}`
        }
        return this.tx('all_time', 'All time')
    },
    grossMargin() {
        if (!this.plSummary.revenue) return 0
        return (this.plSummary.grossProfit / this.plSummary.revenue) * 100
    },
    accountantKpis() {
        return [
            {
                key: 'vat_due',
                label: this.tx('vat_due', 'VAT due'),
                value: this.formatMoney(this.plSummary.vatDue),
                helper: this.plSummary.vatDue >= 0 ? this.tx('payable_to_tax_authority', 'Payable to tax authority') : this.tx('vat_credit_available', 'VAT credit available'),
                icon: 'bi-percent',
                iconClass: this.plSummary.vatDue >= 0 ? 'is-danger' : 'is-success',
                valueClass: this.plSummary.vatDue >= 0 ? 'text-danger' : 'text-success'
            },
            {
                key: 'gross_profit',
                label: this.tx('gross_profit', 'Gross profit'),
                value: this.formatMoney(this.plSummary.grossProfit),
                helper: `${this.grossMargin.toFixed(1)}% ${this.tx('gross_margin', 'gross margin')}`,
                icon: this.plSummary.grossProfit >= 0 ? 'bi-graph-up-arrow' : 'bi-graph-down-arrow',
                iconClass: this.plSummary.grossProfit >= 0 ? 'is-success' : 'is-danger',
                valueClass: this.plSummary.grossProfit >= 0 ? 'text-success' : 'text-danger'
            },
            {
                key: 'receivables',
                label: this.tx('receivables', 'Receivables'),
                value: this.formatMoney(this.salesStats?.dueInvoices?.amount || 0),
                helper: `${this.salesStats?.dueInvoices?.count || 0} ${this.$t('invoices')}`,
                icon: 'bi-arrow-down-left-circle',
                iconClass: 'is-primary',
                valueClass: 'text-primary'
            },
            {
                key: 'payables',
                label: this.tx('payables', 'Payables'),
                value: this.formatMoney(this.purchasesStats?.dueInvoices?.amount || 0),
                helper: `${this.purchasesStats?.dueInvoices?.count || 0} ${this.$t('invoices')}`,
                icon: 'bi-arrow-up-right-circle',
                iconClass: 'is-warning',
                valueClass: 'text-warning'
            }
        ]
    },
    reviewQueues() {
        return [
            {
                key: 'sales_review',
                label: this.tx('sales_to_review', 'Sales to review'),
                value: this.salesInvoicesCount || this.salesInvoices.length || 0,
                helper: this.tx('invoices_loaded_for_period', 'Invoices loaded for this period'),
                icon: 'bi-receipt',
                iconClass: 'text-primary'
            },
            {
                key: 'purchase_review',
                label: this.tx('purchases_to_review', 'Purchases to review'),
                value: this.supplierInvoicesCount || this.supplierInvoices.length || 0,
                helper: this.tx('supplier_docs_ready_for_check', 'Supplier documents ready for check'),
                icon: 'bi-journal-check',
                iconClass: 'text-success'
            },
            {
                key: 'missing_refs',
                label: this.tx('missing_references', 'Missing references'),
                value: this.missingAccountingReferences,
                helper: this.tx('ice_or_payment_reference_missing', 'ICE or payment reference missing'),
                icon: 'bi-shield-exclamation',
                iconClass: this.missingAccountingReferences ? 'text-warning' : 'text-success'
            }
        ]
    },
    missingAccountingReferences() {
        const salesMissing = this.salesInvoices.filter(inv => {
            const ice = inv?.client?.vat_number || inv?.client?.tax_id
            return !ice || this.paymentReference(inv) === '-'
        }).length
        const purchasesMissing = this.supplierInvoices.filter(inv => {
            const ice = inv?.supplier?.vat_number || inv?.supplier?.tax_id
            return !ice || this.paymentReference(inv) === '-'
        }).length
        return salesMissing + purchasesMissing
    },
    accountingAlerts() {
        const alerts = []
        const receivables = this.salesStats?.dueInvoices?.amount || 0
        const payables = this.purchasesStats?.dueInvoices?.amount || 0

        if (this.plSummary.vatDue > 0) {
            alerts.push({
                key: 'vat_due',
                title: this.tx('vat_due', 'VAT due'),
                text: this.formatMoney(this.plSummary.vatDue)
            })
        }
        if (receivables > 0) {
            alerts.push({
                key: 'receivables',
                title: this.tx('unpaid_sales', 'Unpaid sales'),
                text: this.formatMoney(receivables)
            })
        }
        if (payables > 0) {
            alerts.push({
                key: 'payables',
                title: this.tx('unpaid_purchases', 'Unpaid purchases'),
                text: this.formatMoney(payables)
            })
        }
        if (this.missingAccountingReferences > 0) {
            alerts.push({
                key: 'missing_refs',
                title: this.tx('missing_references', 'Missing references'),
                text: `${this.missingAccountingReferences} ${this.tx('documents_need_review', 'documents need review')}`
            })
        }
        return alerts
    },
    plSummary() {
        const salesTTC = this.salesStats?.summary?.totalSales || 0
        const salesTax = this.salesStats?.summary?.totalTax || 0
        const revenue = salesTTC - salesTax

        const purchasesTTC = this.purchasesStats?.summary?.totalPurchases || 0
        // If purchases API doesn't return totalTax, we fallback to 0
        const purchasesTax = this.purchasesStats?.summary?.totalTax || 0
        const expenses = purchasesTTC - purchasesTax

        const grossProfit = revenue - expenses
        const vatCollected = salesTax
        const vatDeductible = purchasesTax
        const vatDue = vatCollected - vatDeductible

        return { revenue, expenses, grossProfit, vatCollected, vatDeductible, vatDue }
    }
  },
  watch : {
    overviewRange(value){
      this.syncDateRange('overview', value)
    },
    purchasesRange(value){
      this.syncDateRange('purchases', value)
    },
    salesRange(value){
      this.syncDateRange('sales', value)
    },
    attachmentsModalOpen(isOpen){
      document.body.classList.toggle('modal-open', isOpen)
    }
  },
  methods : {
    sortSales(field) {
      this.toggleSort(field, 'salesSortField', 'salesSortDirection')
      this.getSalesInvoices(1)
    },
    sortPurchases(field) {
      this.toggleSort(field, 'purchasesSortField', 'purchasesSortDirection')
      this.getSupplierInvoices(1)
    },
    // Shared
    tx(key, fallback) {
        const translated = this.$t(key)
        return translated === key ? fallback : translated
    },
    setActiveTab(tab) {
        if (['overview', 'sales', 'purchases'].includes(tab)) {
            this.activeTab = tab
        }
    },
    formatCompactNumber(value) {
        const number = Number(value) || 0
        try {
            return new Intl.NumberFormat(this.$i18n?.locale || undefined, {
                notation: number >= 1000 ? 'compact' : 'standard',
                maximumFractionDigits: 1
            }).format(number)
        } catch (e) {
            return String(number)
        }
    },
    formatTimestamp(value) {
        return value ? moment(value).format('DD MMM, HH:mm') : ''
    },
    invoiceCountLabel(count) {
        const total = Number(count) || 0
        const label = total === 1 ? this.tx('invoice', 'invoice') : this.tx('invoices', 'invoices')
        return `${this.formatCompactNumber(total)} ${label}`
    },
    paginationLabel(page, totalPages) {
        return `${this.tx('page', 'Page')} ${page} ${this.tx('of', 'of')} ${totalPages}`
    },
    filterSummary(filter) {
        const label = this.$t(filter.key)
        if (filter.key === 'client' || filter.key === 'supplier') {
            return `${label}: ${filter.label || this.tx('not_selected', 'Not selected')}`
        }
        if (filter.key === 'total_amount') {
            return `${label}: ${this.$t(`operator_${filter.operator}`)} ${filter.value}`
        }
        return `${label}: ${filter.value ? this.$t(filter.value) : '—'}`
    },
    handleKeydown(event) {
        if (event.key === 'Escape' && this.attachmentsModalOpen) {
            this.closeAttachmentModal()
        }
    },
    notifyError(error, fallback) {
        const message = error?.response?.data?.message || error?.message || fallback
        if (this.$toast && typeof this.$toast.error === 'function') {
            this.$toast.error(message)
        }
    },
    presetDateRange(option) {
        const today = moment()
        const presets = {
            today: [today.clone().startOf('day'), today.clone().endOf('day')],
            yesterday: [
                today.clone().subtract(1, 'days').startOf('day'),
                today.clone().subtract(1, 'days').endOf('day')
            ],
            this_week: [today.clone().startOf('week'), today.clone().endOf('week')],
            last_week: [
                today.clone().subtract(1, 'weeks').startOf('week'),
                today.clone().subtract(1, 'weeks').endOf('week')
            ],
            this_month: [today.clone().startOf('month'), today.clone().endOf('month')]
        }
        return presets[option] || null
    },
    syncDateRange(scope, range) {
        if (Array.isArray(range) && range[0] && !range[1]) return
        const hasCompleteRange = Array.isArray(range) && range[0] && range[1]
        this[`${scope}Start`] = hasCompleteRange ? moment(range[0]).format('YYYY-MM-DD') : null
        this[`${scope}End`] = hasCompleteRange ? moment(range[1]).format('YYYY-MM-DD') : null

        if (scope === 'overview') this.getStats()
        if (scope === 'sales') this.getSalesInvoices(1)
        if (scope === 'purchases') this.getSupplierInvoices(1)
    },
    applyDatePreset(scope) {
        const option = this[`${scope}DateRangeOption`]
        if (option === 'custom') return
        const preset = this.presetDateRange(option)
        this[`${scope}Range`] = preset ? preset.map(date => date.toDate()) : [null, null]
    },
    async fetchPaymentMethods() {
        try {
            const response = await helper.customGet(`${process.env.VUE_APP_API}/payment-methods?per_page=100`)
            const methods = response.data.data.filter(m => m.is_active).map(m => ({ value: m.name, label: m.name }));
            
            // Add to sales filters
            const salesPm = this.salesAvailableFilters.find(f => f.key === 'payment_method');
            if(salesPm) salesPm.options = methods;

            // Add to purchases filters
            const purchasesPm = this.purchasesAvailableFilters.find(f => f.key === 'payment_method');
            if(purchasesPm) purchasesPm.options = methods;

        } catch (e) {
          this.notifyError(e, this.tx('payment_methods_unavailable', 'Payment methods could not be loaded.'))
        }
    },
    formatDate(date) { return helper.formatDate(date) },
    formatMoney(number) { return helper.formatMoney(number) },
    
    // --- Sales Filters Methods ---
    addSalesFilter(filter) {
        if (filter.key === 'total_amount') {
             this.salesActiveFilters.push({ key: filter.key, operator: 'gt', value: 0 });
        } else if (filter.key === 'client') {
             this.salesActiveFilters.push({ key: filter.key, value: null, label: '' });
        } else {
            const defaultVal = filter.options[0]?.value;
            this.salesActiveFilters.push({ key: filter.key, value: defaultVal });
        }
    },
    removeSalesFilter(index) {
        this.salesActiveFilters.splice(index, 1);
        this.getSalesInvoices(1);
    },
    clearSalesFilters() {
        this.salesActiveFilters = [];
        this.getSalesInvoices(1);
    },
    applySalesFilters() {
        this.getSalesInvoices(1);
    },
    getSalesFilterOptions(key) {
        const filter = this.salesAvailableFilters.find(f => f.key === key);
        return filter ? filter.options : [];
    },
    async searchClients(input) {
        if (input.length < 1) { return [] }
        const res = await helper.customGet(`${process.env.VUE_APP_API}/clients?keyword=${encodeURIComponent(input)}&per_page=10`)
        return res.data.data.map(client => ({
             title: client.name,
             id: client.id,
             ...client
        }));
    },
    handleClientSelect(result, filterIndex) {
        if(result){
            this.salesActiveFilters[filterIndex].value = result.id;
            this.salesActiveFilters[filterIndex].label = result.name;
        }
    },

    // --- Purchases Filters Methods ---
    addPurchasesFilter(filter) {
        if (filter.key === 'total_amount') {
             this.purchasesActiveFilters.push({ key: filter.key, operator: 'gt', value: 0 });
        } else if (filter.key === 'supplier') {
             this.purchasesActiveFilters.push({ key: filter.key, value: null, label: '' });
        } else {
            const defaultVal = filter.options[0]?.value;
            this.purchasesActiveFilters.push({ key: filter.key, value: defaultVal });
        }
    },
    removePurchasesFilter(index) {
        this.purchasesActiveFilters.splice(index, 1);
        this.getSupplierInvoices(1);
    },
    clearPurchasesFilters() {
        this.purchasesActiveFilters = [];
        this.getSupplierInvoices(1);
    },
    applyPurchasesFilters() {
        this.getSupplierInvoices(1);
    },
    getPurchasesFilterOptions(key) {
        const filter = this.purchasesAvailableFilters.find(f => f.key === key);
        return filter ? filter.options : [];
    },
    async searchSuppliers(input) {
        if (input.length < 1) { return [] }
        const res = await helper.customGet(`${process.env.VUE_APP_API}/suppliers?keyword=${encodeURIComponent(input)}&per_page=10`)
        return res.data.data.map(supplier => ({
             title: supplier.name,
             id: supplier.id,
             ...supplier
        }));
    },
    handleSupplierSelect(result, filterIndex) {
        if(result){
            this.purchasesActiveFilters[filterIndex].value = result.id;
            this.purchasesActiveFilters[filterIndex].label = result.name;
        }
    },
    
    // Stats
    async getStats(){
        const requestId = ++this.statsRequestId
        this.loadingStats = true
        this.statsError = null
        try {
            let salesUrl = `${process.env.VUE_APP_API}/invoices/insights/sales?1=1`
            let purchasesUrl = `${process.env.VUE_APP_API}/supplier-invoices/insights/purchases?1=1`
            let bankUrl = `${process.env.VUE_APP_API}/bank-accounts/summary/all?1=1`
            
            if(this.overviewStart && this.overviewEnd){
               salesUrl += `&start=${this.overviewStart}&end=${this.overviewEnd}`
               purchasesUrl += `&start=${this.overviewStart}&end=${this.overviewEnd}`
               bankUrl += `&start=${this.overviewStart}&end=${this.overviewEnd}`
            }

            const [salesRes, purchasesRes, bankRes] = await Promise.all([
                helper.customGet(salesUrl),
                helper.customGet(purchasesUrl),
                helper.customGet(bankUrl).catch(() => ({ data: null }))
            ])

            if (requestId !== this.statsRequestId) return
            this.salesStats = salesRes.data
            this.purchasesStats = purchasesRes.data
            this.bankSummary = bankRes.data
            this.statsUpdatedAt = new Date()
        } catch(e) {
            if (requestId !== this.statsRequestId) return
            this.statsError = e
        } finally {
            if (requestId === this.statsRequestId) {
                this.loadingStats = false
            }
        }
    },
    exportVATDeclaration() {
        const doc = new jsPDF()
        const pl = this.plSummary
        const business = this.$store.state.business
        const period = this.overviewStart && this.overviewEnd 
            ? `${this.overviewStart} → ${this.overviewEnd}` 
            : this.$t('all_time')

        // Header
        doc.setFontSize(18)
        doc.text(this.$t('vat_declaration'), 105, 20, { align: 'center' })
        doc.setFontSize(11)
        doc.text(business?.name || '', 105, 28, { align: 'center' })
        doc.setFontSize(9)
        doc.text(`${this.$t('period')}: ${period}`, 105, 35, { align: 'center' })

        let y = 50
        doc.setFontSize(12)
        doc.setFont(undefined, 'bold')
        doc.text(this.$t('profit_loss_statement'), 14, y)
        y += 10

        doc.setFontSize(10)
        doc.setFont(undefined, 'normal')
        const rows = [
            [this.$t('total_revenue_ht'), helper.formatMoney(pl.revenue)],
            [this.$t('total_expenses_ht'), helper.formatMoney(pl.expenses)],
            [this.$t('gross_profit'), helper.formatMoney(pl.grossProfit)],
            ['', ''],
            [this.$t('vat_collected'), helper.formatMoney(pl.vatCollected)],
            [this.$t('vat_deductible'), helper.formatMoney(pl.vatDeductible)],
            [this.$t('vat_due'), helper.formatMoney(pl.vatDue)],
        ]
        rows.forEach(([label, value]) => {
            if (!label) { y += 5; return }
            doc.text(label, 14, y)
            doc.text(value, 190, y, { align: 'right' })
            y += 8
        })

        // Cash flow if available
        if (this.bankSummary) {
            y += 10
            doc.setFont(undefined, 'bold')
            doc.text(this.$t('cash_flow_overview'), 14, y)
            y += 10
            doc.setFont(undefined, 'normal')
            const cfRows = [
                [this.$t('incoming_total'), helper.formatMoney(this.bankSummary.cash_flow?.incoming || 0)],
                [this.$t('outgoing_total'), helper.formatMoney(this.bankSummary.cash_flow?.outgoing || 0)],
                [this.$t('net_flow_total'), helper.formatMoney(this.bankSummary.cash_flow?.net || 0)],
                [this.$t('total_balance'), helper.formatMoney(this.bankSummary.total_balance || 0)],
            ]
            cfRows.forEach(([label, value]) => {
                doc.text(label, 14, y)
                doc.text(value, 190, y, { align: 'right' })
                y += 8
            })
        }

        doc.save(`vat_declaration_${moment().format('YYYY-MM-DD')}.pdf`)
        this.$toast.success(this.$t('download_pdf'))
    },
    handleOverviewDateRangeChange() {
      this.applyDatePreset('overview')
    },

    // Data fetchers
    async getSupplierInvoices(page){
      const requestId = ++this.purchasesRequestId
      this.loadingSupplierInvoices = true
      this.purchasesError = null
      try{
        this.supplierInvoicesPage = Math.max(Number(page) || 1, 1);
        let url = `${process.env.VUE_APP_API}/supplier-invoices?page=${this.supplierInvoicesPage}&per_page=${this.supplierInvoicesPerPage}&sort_field=${this.purchasesSortField}&sort_type=${this.purchasesSortDirection}`;
        if(this.purchasesStart && this.purchasesEnd){
          url += `&start=${this.purchasesStart}&end=${this.purchasesEnd}`
        }
        
        // Apply Filters
        this.purchasesActiveFilters.forEach(filter => {
            if (filter.key === 'payment_method' && filter.value) {
                url += `&payment_method=${encodeURIComponent(filter.value)}`;
            } else if (filter.key === 'total_amount' && filter.value !== '') {
                url += `&amount_operator=${encodeURIComponent(filter.operator)}&amount_value=${encodeURIComponent(filter.value)}`;
            } else if (filter.key === 'supplier' && filter.value) {
                url += `&supplier_id=${encodeURIComponent(filter.value)}`;
            }
        });

        const response = await helper.customGet(url);
        if (requestId !== this.purchasesRequestId) return
        this.supplierInvoices = response.data.data || [];
        this.supplierInvoicesCount = Number(response.data.count) || 0;
      } catch(error){
        if (requestId !== this.purchasesRequestId) return
        this.purchasesError = error
      } finally {
        if (requestId === this.purchasesRequestId) {
          this.loadingSupplierInvoices = false
        }
      }
    },
    async getSalesInvoices(page){
      const requestId = ++this.salesRequestId
      this.loadingSalesInvoices = true
      this.salesError = null
      try{
        this.salesInvoicesPage = Math.max(Number(page) || 1, 1);
        let url = `${process.env.VUE_APP_API}/invoices?page=${this.salesInvoicesPage}&per_page=${this.salesInvoicesPerPage}&sort_field=${this.salesSortField}&sort_type=${this.salesSortDirection}`;

        if(this.salesStart && this.salesEnd){
          url += `&start=${this.salesStart}&end=${this.salesEnd}`
        }
        
        // Apply Filters
        this.salesActiveFilters.forEach(filter => {
            if (filter.key === 'payment_method' && filter.value) {
                url += `&payment_method=${encodeURIComponent(filter.value)}`;
            } else if (filter.key === 'total_amount' && filter.value !== '') {
                url += `&amount_operator=${encodeURIComponent(filter.operator)}&amount_value=${encodeURIComponent(filter.value)}`;
            } else if (filter.key === 'client' && filter.value) {
                url += `&client_id=${encodeURIComponent(filter.value)}`;
            }
        });

        const response = await helper.customGet(url);
        if (requestId !== this.salesRequestId) return
        this.salesInvoices = response.data.data || [];
        this.salesInvoicesCount = Number(response.data.count) || 0;
      } catch(error){
        if (requestId !== this.salesRequestId) return
        this.salesError = error
      } finally {
        if (requestId === this.salesRequestId) {
          this.loadingSalesInvoices = false
        }
      }
    },

   // Calculations
   calcSubTotal(row){
     if(!row || !row.products || !row.products.length) return 0
     return row.products.map(p => Number(p.sub_total || 0)).reduce((a,c)=>a+c,0)
   },
   calcTotal(row){
     if(!row || !row.products || !row.products.length) return 0
     if(row.status === 'draft') return 0
     return row.products.map(p => Number(p.total || 0)).reduce((a,c)=>a+c,0)
   },
   calcVAT(row){
     const total = this.calcTotal(row)
     const sub = this.calcSubTotal(row)
     return Math.max(total - sub, 0)
   },
   paymentDate(row){
     const t = (row?.transactions || []).find(tr => tr.status === 'paid')
     return t ? this.formatDate(t.created_at || t.date) : '-'
   },
   paymentMethod(row){
     const t = (row?.transactions || []).find(tr => tr.status === 'paid')
     return t ? (t.payment_method || t.method || '-') : '-'
   },
  paymentReference(row){
    const t = (row?.transactions || []).find(tr => tr.status === 'paid')
    if (t) {
     if (t.pm_reference) return t.pm_reference
     if (t.reference) return t.reference
    }
    return '-'
  },

   // Exports
   exportPurchasesPDF(){
     const doc = new jsPDF();
     const data = this.supplierInvoices.map(inv => ([
       inv?.supplier?.vat_number || inv?.supplier?.tax_id || '-',
       inv?.supplier?.name || '-',
       inv.reference,
       this.formatDate(inv.date),
       this.formatMoney(this.calcSubTotal(inv)),
       this.formatMoney(this.calcVAT(inv)),
       this.formatMoney(this.calcTotal(inv)),
       this.paymentDate(inv),
       this.$t(this.paymentMethod(inv)),
       this.paymentReference(inv)
     ]))
     doc.text("Achats", 15, 10);
     autoTable(doc, { 
      head: [["ICE","Fournisseur","Facture","Date","Total HT","TVA","Total TTC","Date De Paiment","Methode de Paiment","Reference De paiment"]], 
      body: data,
      styles: {
        fontSize: 7, // Default is 10
        cellPadding: 1 // Default is 5
      }
    })
     doc.save('Achats.pdf')
   },
   exportSalesPDF(){
    const doc = new jsPDF();
    const data = this.salesInvoices.map(inv => ([
      inv?.client?.vat_number || inv?.client?.tax_id || '-',
      inv?.client?.name || '-',
      inv.reference,
      this.formatDate(inv.date),
      this.formatMoney(this.calcSubTotal(inv)),
      this.formatMoney(this.calcVAT(inv)),
      this.formatMoney(this.calcTotal(inv)),
      this.paymentDate(inv),
      this.$t(this.paymentMethod(inv)),
      this.paymentReference(inv)
  ]));

doc.text("Ventes", 15, 10);
autoTable(doc, {
  head: [["ICE","Client","Facture","Date","Total HT","TVA","Total TTC","Date De Paiment","Methode de Paiment","Reference De paiment"]],
  body: data,
  styles: {
    fontSize: 7, // Default is 10
    cellPadding: 1 // Default is 5
  }
});
doc.save('Ventes.pdf');
   },
   // Excel Exports
   exportPurchasesExcel(){
     const headers = [
       'ICE',
       'Fournisseur',
       'Facture',
       'Date',
       'TotalHt',
       'TVA',
       'TotalTTC',
       'Date_de_paiment',
       'Methode_de_paiment',
       'Reference_de_paiment'
     ]
     const rows = this.supplierInvoices.map(inv => ({
       ICE: inv?.supplier?.vat_number || inv?.supplier?.tax_id || '-',
       Fournisseur: inv?.supplier?.name || '-',
       Facture: inv.reference,
       Date: this.formatDate(inv.date),
       TotalHt: this.formatMoney(this.calcSubTotal(inv)),
       TVA: this.formatMoney(this.calcVAT(inv)),
       TotalTTC: this.formatMoney(this.calcTotal(inv)),
       Date_de_paiment: this.paymentDate(inv),
       Methode_de_paiment: this.paymentMethod(inv),
       Reference_de_paiment: this.paymentReference(inv)
     }))
     const wb = XLSX.utils.book_new()
     const ws = XLSX.utils.json_to_sheet(rows, { header: headers })
     XLSX.utils.book_append_sheet(wb, ws, 'Purchases')
     XLSX.writeFile(wb, 'purchases.xlsx')
   },
   exportSalesExcel(){
     const headers = [
       'ICE',
       'Client',
       'Facture',
       'Date',
       'TotalHt',
       'TVA',
       'TotalTTC',
       'Date_de_paiment',
       'Methode_de_paiment',
       'Reference_de_paiment'
     ]
     const rows = this.salesInvoices.map(inv => ({
       ICE: inv?.client?.vat_number || inv?.client?.tax_id || '-',
       Client: inv?.client?.name || '-',
       Facture: inv.reference,
       Date: this.formatDate(inv.date),
       TotalHt: this.formatMoney(this.calcSubTotal(inv)),
       TVA: this.formatMoney(this.calcVAT(inv)),
       TotalTTC: this.formatMoney(this.calcTotal(inv)),
       Date_de_paiment: this.paymentDate(inv),
       Methode_de_paiment: this.paymentMethod(inv),
       Reference_de_paiment: this.paymentReference(inv)
     }))
     const wb = XLSX.utils.book_new()
     const ws = XLSX.utils.json_to_sheet(rows, { header: headers })
     XLSX.utils.book_append_sheet(wb, ws, 'Sales')
     XLSX.writeFile(wb, 'sales.xlsx')
   },
   downloadCSV(rows, filename){
     if(!rows || !rows.length) return
     const headers = Object.keys(rows[0])
     const csvContent = [headers.join(',')]
       .concat(rows.map(r => headers.map(h => {
         const val = (r[h] ?? '').toString().replace(/"/g,'""')
         return `"${val}"`
       }).join(',')))
       .join('\n')
     const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
     const link = document.createElement('a')
     const url = URL.createObjectURL(blob)
     link.setAttribute('href', url)
     link.setAttribute('download', filename)
     link.style.visibility = 'hidden'
     document.body.appendChild(link)
     link.click()
     document.body.removeChild(link)
     URL.revokeObjectURL(url)
   },



   // Selection helpers
   toggleSelectAll(type, event){
     const checked = event.target.checked
     if(type === 'purchases'){
       this.selectedPurchases = checked ? this.supplierInvoices.map(i => i.id) : []
     }else{
       this.selectedSales = checked ? this.salesInvoices.map(i => i.id) : []
     }
   },

   // Use template modal to generate real invoice PDFs
   openInvoiceTemplate(row, type){
     // Map our row to expected data format for the template modal
     const data = {
       ...row,
       sub_total: this.calcSubTotal(row),
       total_vat: this.calcVAT(row),
       total: this.calcTotal(row)
     }
     this.invoiceModalType = type
     this.invoiceModalData = data
     this.invoiceModalOpen = true
   },
   // Attachments modal
   openAttachmentModal(row){
     this.currentAttachmentEntityId = row && row.id ? row.id : null
     this.attachmentsModalOpen = !!this.currentAttachmentEntityId
   },
   closeAttachmentModal(){
     this.attachmentsModalOpen = false
     this.currentAttachmentEntityId = null
   },
   async waitForPdfReady(){
     // Wait until the child modal generates pdfUrl
     const timeoutMs = 15000
     const pollInterval = 250
     const maxTries = Math.ceil(timeoutMs / pollInterval)
     for(let i = 0; i < maxTries; i++){
       const child = this.$refs.invoiceTpl
       if(child && child.pdfUrl){
         return true
       }
       await new Promise(r => setTimeout(r, pollInterval))
     }
     return false
   },
   async downloadSelectedPurchasesPDF(){
     const ids = new Set(this.selectedPurchases)
     const queue = this.supplierInvoices.filter(inv => ids.has(inv.id))
     if(!queue.length) return
     this.processingBulk = true
     for(const row of queue){
       this.openInvoiceTemplate(row, 'supplier-invoice')
       await this.$nextTick()
       const ok = await this.waitForPdfReady()
       if(ok && this.$refs.invoiceTpl){
         this.$refs.invoiceTpl.downloadPdf()
       }
       this.invoiceModalOpen = false
       await new Promise(r => setTimeout(r, 300))
     }
     this.processingBulk = false
   },
   async downloadSelectedSalesPDF(){
     const ids = new Set(this.selectedSales)
     const queue = this.salesInvoices.filter(inv => ids.has(inv.id))
     if(!queue.length) return
     this.processingBulk = true
     for(const row of queue){
       this.openInvoiceTemplate(row, 'invoice')
       await this.$nextTick()
       const ok = await this.waitForPdfReady()
       if(ok && this.$refs.invoiceTpl){
         this.$refs.invoiceTpl.downloadPdf()
       }
       this.invoiceModalOpen = false
       await new Promise(r => setTimeout(r, 300))
     }
     this.processingBulk = false
   },

   // Date range handlers
   handlePurchasesDateRangeChange() {
     this.applyDatePreset('purchases')
   },

   handleSalesDateRangeChange() {
     this.applyDatePreset('sales')
   }
  }
}
</script>
<style scoped>
  .accountant-page{
    --accounting-accent: #303030;
    --accounting-accent-dark: #15181c;
    --accounting-border: rgba(0,0,0,.125);
    --accounting-ink: #15181c;
    --accounting-muted: #67798e;
    --accounting-soft: #f1f5fb;
    --accounting-shadow: 0 .125rem .25rem rgba(0,0,0,.075);
    color: var(--accounting-ink);
    padding-bottom: 28px;
  }
  .accounting-hero{
    background: transparent;
    border: 0;
    box-shadow: none;
    color: var(--accounting-ink);
  }
  .accounting-hero-layout{
    align-items: center;
    display: flex;
    gap: 32px;
    justify-content: space-between;
  }
  .accounting-title{
    color: var(--accounting-ink);
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.2;
    margin: 0 0 5px;
  }
  .accounting-subtitle{
    color: var(--accounting-muted);
    line-height: 1.5;
    margin: 0;
    max-width: 620px;
  }
  .accounting-hero-actions,
  .accounting-ledger-actions{
    align-items: flex-end;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: flex-end;
  }
  .accounting-period-control{
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  .accounting-control-label{
    color: var(--accounting-muted);
    font-size: 10px;
    font-weight: 800;
    letter-spacing: .08em;
    line-height: 1;
    text-transform: uppercase;
  }
  .accounting-period-select{
    min-width: 210px;
  }
  .accounting-hero .form-select{
    background-color: #fff;
    border-color: #c2c9d2;
  }
  .accounting-refresh-btn{
    align-items: center;
    display: inline-flex;
    height: 38px;
    justify-content: center;
    width: 40px;
  }
  .accounting-export-btn{
    font-weight: 600;
    min-height: 38px;
  }
  .accounting-custom-period{
    align-items: center;
    border-top: 1px solid #e1e4e8;
    display: flex;
    gap: 16px;
    justify-content: flex-end;
    margin-top: 20px;
    padding-top: 18px;
  }
  .accounting-custom-period .text-muted{
    color: var(--accounting-muted) !important;
  }
  .accounting-date-picker{
    max-width: 360px;
    width: 100%;
  }
  .accounting-tabs{
    border-bottom: 1px solid #c2c9d2;
    display: flex;
    gap: 0;
    padding: 0;
    width: 100%;
  }
  .accounting-tabs .nav-link{
    align-items: center;
    background: transparent;
    border: 1px solid transparent;
    border-radius: .25rem .25rem 0 0;
    color: #637482;
    display: flex;
    font-weight: 650;
    gap: 5px;
    margin-bottom: -1px;
    padding: .75rem 1rem;
  }
  .accounting-tabs .nav-link:hover{
    border-color: #e1e4e8 #e1e4e8 #c2c9d2;
    color: var(--accounting-accent);
  }
  .accounting-tabs .nav-link.active{
    background: #fff;
    border-color: #c2c9d2 #c2c9d2 #fff;
    color: var(--accounting-accent-dark);
  }
  .accounting-tab-count,
  .accounting-filter-count{
    align-items: center;
    background: #e1e4e8;
    border-radius: 999px;
    display: inline-flex;
    font-size: 10px;
    font-weight: 800;
    height: 19px;
    justify-content: center;
    margin-left: 2px;
    min-width: 19px;
    padding: 0 5px;
  }
  .accounting-section-heading,
  .accounting-ledger-heading{
    align-items: flex-end;
    display: flex;
    gap: 24px;
    justify-content: space-between;
    margin-bottom: 17px;
  }
  .accounting-section-heading h2,
  .accounting-ledger-heading h2{
    color: var(--accounting-ink);
    font-size: 1.2rem;
    font-weight: 750;
    letter-spacing: -.015em;
    margin: 2px 0 0;
  }
  .accounting-section-kicker{
    color: var(--accounting-muted);
    display: block;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: .1em;
    text-transform: uppercase;
  }
  .accounting-period-badge{
    background: #f1f5fb;
    border: 1px solid #e1e4e8;
    border-radius: 999px;
    color: var(--accounting-accent-dark);
    display: inline-block;
    font-size: 12px;
    font-weight: 700;
    padding: 6px 10px;
  }
  .accounting-updated-at{
    color: var(--accounting-muted);
    display: block;
    font-size: 10px;
    margin-top: 5px;
  }
  .accounting-kpi-grid{
    display: grid;
    gap: 14px;
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  .accounting-kpi,
  .accounting-panel{
    background: #fff;
    border: 1px solid var(--accounting-border);
    border-radius: .25rem;
    box-shadow: var(--accounting-shadow);
  }
  .accounting-kpi{
    border-left: 4px solid var(--accounting-accent);
    min-height: 132px;
    padding: 1rem;
    position: relative;
  }
  .accounting-kpi.is-success{ border-left-color: #17bb84; }
  .accounting-kpi.is-warning{ border-left-color: #f67a28; }
  .accounting-kpi.is-danger{ border-left-color: #e85444; }
  .accounting-kpi-copy{
    min-width: 0;
  }
  .accounting-kpi-label{
    color: var(--accounting-muted);
    display: block;
    font-size: 12px;
    font-weight: 650;
    margin-bottom: 7px;
  }
  .accounting-kpi-value{
    font-size: clamp(1.2rem, 2vw, 1.55rem);
    font-weight: 700;
    line-height: 1.25;
    margin: 0 0 5px;
    overflow-wrap: anywhere;
  }
  .accounting-kpi-icon{
    align-items: center;
    border-radius: .25rem;
    display: inline-flex;
    flex: 0 0 42px;
    font-size: 18px;
    height: 42px;
    justify-content: center;
    margin-left: 10px;
    width: 42px;
  }
  .accounting-kpi-icon.is-primary{ background: rgba(48,48,48,.1); color: #303030; }
  .accounting-kpi-icon.is-success{ background: #e8f8f3; color: #17bb84; }
  .accounting-kpi-icon.is-warning{ background: #fef2ea; color: #f67a28; }
  .accounting-kpi-icon.is-danger{ background: #fdeEec; color: #e85444; }
  .accounting-panel{
    padding: 1rem;
  }
  .accounting-review-item{
    background: var(--accounting-soft);
    border: 1px solid #e1e4e8;
    border-radius: .25rem;
    min-height: 122px;
    padding: 15px;
  }
  .accounting-review-item h5{
    color: var(--accounting-ink);
    font-size: 1.3rem;
    font-weight: 800;
  }
  .accounting-alert{
    align-items: flex-start;
    border-bottom: 1px solid #edf0f4;
    display: flex;
    gap: 5px;
    padding: 11px 0;
  }
  .accounting-alert:first-child{ padding-top: 0; }
  .accounting-alert:last-child{ border-bottom: 0; padding-bottom: 0; }
  .accounting-inline-loading,
  .accounting-table-loading{
    align-items: center;
    color: var(--accounting-muted);
    display: flex;
    font-size: 12px;
    gap: 8px;
    justify-content: center;
    padding: 14px;
  }
  .accountant-page .card{
    border: 1px solid var(--accounting-border);
    border-radius: .25rem;
    box-shadow: var(--accounting-shadow);
    overflow: hidden;
  }
  .accountant-page .card-header{
    border-bottom-color: var(--accounting-border);
    padding: 15px 18px;
  }
  .accounting-pl-summary{
    border: 1px solid transparent;
    border-radius: .25rem;
    min-height: 104px;
    padding: 16px;
  }
  .accounting-pl-summary.is-positive{
    background: #eff9f3;
    border-color: #d8eee1;
  }
  .accounting-pl-summary.is-negative{
    background: #fff3f4;
    border-color: #f2dcdf;
  }
  .accounting-vat-metric{
    background: #f8fafb;
    border: 1px solid var(--accounting-border);
    border-radius: .25rem;
    padding: 15px;
    text-align: center;
  }
  .accounting-vat-metric.is-due{
    background: #fff7f7;
    border-color: #ecc9cd;
  }
  .accounting-vat-metric.is-credit{
    background: #f2faf5;
    border-color: #cce7d6;
  }
  .accounting-error-state{
    align-items: center;
    background: #fff6f6;
    border: 1px solid #f4d5d8;
    border-radius: .25rem;
    color: #8d2934;
    display: flex;
    gap: 12px;
    padding: 13px 15px;
  }
  .accounting-error-icon{
    align-items: center;
    background: #fbe5e7;
    border-radius: .25rem;
    display: inline-flex;
    flex: 0 0 36px;
    height: 36px;
    justify-content: center;
    width: 36px;
  }
  .accounting-filter-bar{
    align-items: center;
    background: #fff;
    border: 1px solid var(--accounting-border);
    border-radius: .25rem;
    display: flex;
    flex-wrap: wrap;
    gap: 11px;
    padding: 10px 12px;
  }
  .accounting-filter-menu{
    border: 1px solid var(--accounting-border);
    border-radius: .25rem;
    box-shadow: 0 .5rem 1rem rgba(0,0,0,.15);
    min-width: 330px;
  }
  .accounting-operator-select{
    max-width: 86px;
  }
  .accounting-autocomplete{
    min-width: 0;
  }
  .accounting-filter-chips{
    align-items: center;
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    gap: 7px;
  }
  .accounting-filter-chip{
    align-items: center;
    background: #f1f5fb;
    border: 1px solid #e1e4e8;
    border-radius: 999px;
    color: var(--accounting-accent-dark);
    display: inline-flex;
    font-size: 11px;
    font-weight: 650;
    gap: 4px;
    padding: 5px 5px 5px 10px;
  }
  .accounting-filter-chip button{
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 50%;
    color: inherit;
    display: inline-flex;
    height: 19px;
    justify-content: center;
    padding: 0;
    width: 19px;
  }
  .accounting-filter-chip button:hover{
    background: rgba(48,48,48,.1);
  }
  .accounting-clear-filters{
    background: transparent;
    border: 0;
    color: #b1323f;
    font-size: 11px;
    font-weight: 700;
    padding: 4px 6px;
  }
  .accounting-ledger-heading{
    margin-top: 5px;
  }
  .accounting-ledger-heading p{
    color: var(--accounting-muted);
    font-size: 12px;
    margin: 4px 0 0;
  }
  .accounting-ledger-date-picker{
    min-width: 230px;
    width: 230px;
  }
  .accounting-export-menu{
    min-height: 38px;
  }
  .accounting-table-card{
    background: #fff;
    border: 1px solid #ddd;
    overflow: hidden;
  }
  .accounting-table-wrap{
    max-height: min(64vh, 720px);
  }
  .accounting-table{
    color: #304452;
    min-width: 1180px;
  }
  .accounting-table > :not(caption) > * > *{
    border-bottom: 1px solid #edf1f4;
    padding: 13px 15px !important;
    vertical-align: middle;
  }
  .accounting-table thead th{
    background: #fff;
    color: #71808c;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: .055em;
    position: sticky;
    text-transform: uppercase;
    top: 0;
    white-space: nowrap;
    z-index: 1;
  }
  .accounting-table tbody tr:not(.accounting-total-row):hover{
    background: #f1f5fb;
  }
  .accounting-party-link{
    color: var(--accounting-ink);
    font-weight: 700;
    text-decoration: none;
  }
  .accounting-party-link:hover,
  .accounting-invoice-link:hover{
    color: var(--accounting-accent);
    text-decoration: underline;
  }
  .accounting-invoice-link{
    color: var(--accounting-accent);
    font-weight: 700;
    text-decoration: none;
  }
  .accounting-identifier{
    color: #647582;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 11px;
  }
  .accounting-payment-method{
    background: #f1f5fb;
    border-radius: 999px;
    display: inline-block;
    font-size: 10px;
    font-weight: 700;
    padding: 4px 8px;
    white-space: nowrap;
  }
  .accounting-total-row td{
    background: #f1f5fb !important;
    border-bottom: 0 !important;
    color: var(--accounting-accent-dark);
    font-weight: 800;
  }
  .accounting-empty-state{
    color: var(--accounting-muted);
    padding: 44px 20px;
    text-align: center;
  }
  .accounting-empty-icon{
    align-items: center;
    background: #f1f5fb;
    border-radius: .25rem;
    color: var(--accounting-accent);
    display: inline-flex;
    font-size: 24px;
    height: 54px;
    justify-content: center;
    margin-bottom: 13px;
    width: 54px;
  }
  .accounting-empty-state h3{
    color: var(--accounting-ink);
    font-size: 1rem;
    font-weight: 750;
    margin-bottom: 5px;
  }
  .accounting-empty-state p{
    font-size: 12px;
    margin: 0 auto 12px;
    max-width: 420px;
  }
  .accounting-pagination{
    align-items: center;
    border-top: 1px solid var(--accounting-border);
    color: var(--accounting-muted);
    display: flex;
    font-size: 11px;
    justify-content: space-between;
    padding: 11px 14px;
  }
  .accounting-skeleton{
    animation: accounting-shimmer 1.35s ease-in-out infinite;
    background: linear-gradient(90deg, #edf1f4 25%, #f8fafb 50%, #edf1f4 75%);
    background-size: 200% 100%;
    border-radius: 6px;
    display: block;
  }
  .accounting-skeleton-value{
    height: 28px;
    margin: 7px 0 8px;
    max-width: 145px;
    width: 75%;
  }
  .accounting-skeleton-helper{
    height: 10px;
    max-width: 170px;
    width: 90%;
  }
  .accounting-modal-backdrop{
    background: rgba(11, 28, 38, .58);
    display: block;
  }
  .accounting-spin{
    animation: accounting-spin .85s linear infinite;
  }
  ::v-deep .accounting-autocomplete .autocomplete-input{
    min-height: 31px;
  }
  ::v-deep .accounting-date-picker .mx-input,
  ::v-deep .accounting-ledger-date-picker .mx-input{
    height: 38px;
  }
  @keyframes accounting-spin{
    to{ transform: rotate(360deg); }
  }
  @keyframes accounting-shimmer{
    to{ background-position: -200% 0; }
  }
  @media (max-width: 1199.98px){
    .accounting-hero-layout{
      align-items: flex-start;
      flex-direction: column;
    }
    .accounting-hero-actions{
      justify-content: flex-start;
      width: 100%;
    }
    .accounting-kpi-grid{
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
  @media (max-width: 767.98px){
    .accountant-page .px-8{
      padding-left: 16px !important;
      padding-right: 16px !important;
    }
    .accounting-custom-period{
      align-items: stretch;
      flex-direction: column;
    }
    .accounting-section-heading,
    .accounting-ledger-heading{
      align-items: flex-start;
      flex-direction: column;
    }
    .accounting-ledger-actions{
      justify-content: flex-start;
      width: 100%;
    }
    .accounting-filter-menu{
      min-width: min(330px, calc(100vw - 48px));
    }
  }
  @media (max-width: 575.98px){
    .accounting-hero-layout{
      gap: 20px;
    }
    .accounting-hero-actions{
      align-items: stretch;
      flex-direction: column;
    }
    .accounting-refresh-btn{
      width: 100%;
    }
    .accounting-export-btn{
      width: 100%;
    }
    .accounting-tabs{
      display: flex;
      width: 100%;
    }
    .accounting-tabs .nav-item{
      flex: 1;
      min-width: 0;
    }
    .accounting-tabs .nav-link{
      font-size: 12px;
      justify-content: center;
      padding: 9px 7px;
      width: 100%;
    }
    .accounting-tab-count{
      display: none;
    }
    .accounting-kpi-grid{
      grid-template-columns: 1fr;
    }
    .accounting-period-select,
    .accounting-date-picker,
    .accounting-ledger-date-picker,
    .accounting-period-control{
      max-width: none;
      min-width: 0;
      width: 100%;
    }
    .accounting-ledger-actions{
      align-items: stretch;
      flex-direction: column;
    }
    .accounting-export-menu{
      width: 100%;
    }
    .accounting-error-state{
      align-items: flex-start;
      flex-wrap: wrap;
    }
  }
  @media (prefers-reduced-motion: reduce){
    .accounting-kpi,
    .accounting-skeleton,
    .accounting-spin{
      animation: none;
      transition: none;
    }
  }
</style>
